import { Board } from "../game/board.js";
import { findMatchGroups, energyForGroupSize } from "../game/match.js";
import { resolveGravityAndRefill } from "../game/gravity.js";
import { hasPossibleMoves, shuffleBoard } from "../game/moves.js";
import { HUD } from "../ui/hud.js";
import { MonsterBar } from "../ui/monsterBar.js";
import { getAffectedPositions } from "../game/skills.js";
import { LevelStore } from "../game/levelStore.js";
import { spawnMatchParticles, spawnComboText, spawnSkillBurst, spawnFailEffect, spawnBeamVFX } from "../vfx/particles.js";
import { createBackground } from "../vfx/background.js";
import { Theme, tileColorToHex } from "../config/theme.js";
import { RegionThemes, hexToPhaser } from "../data/regionThemes.js";
import { BESTIARY } from "../data/bestiaryData.js";
import villainImg from "../assets/EvilEntity/villain-front.png";

// ── Region background images ──────────────────────────────────────────────
import bgIgnarion  from "../assets/backgrounds/pazzurumon_ignarion.png";
import bgHydrallis from "../assets/backgrounds/pazzurumon_hydrallys.png";
import bgTerraevel from "../assets/backgrounds/pazzurumon_terravael.png";
import bgVoltrix   from "../assets/backgrounds/pazzurumon_voltaris.png";
import bgAetherion  from "../assets/backgrounds/pazzurumon_aetherion.png";

import { generateLootbox, generateBossLootbox } from "../game/lootbox.js";
import { Collection } from "../game/collection.js";
import { MoodEngine }    from '../game/MoodEngine.js';
import { MoodPresenter } from '../ui/MoodPresenter.js';
import { Settings }      from '../game/Settings.js';
import { I18n }          from '../game/I18n.js';
import { ReplayRecorder } from '../game/ReplayRecorder.js';

// ── Creature sprite map — reward reveal modal (mesmo padrão de BestiaryScene) ─
const _creatureImgMap = import.meta.glob(
  "../assets/monsters/creatures/**/*.png",
  { eager: true, import: "default" }
);
const CREATURE_IMG = {};
for (const [path, url] of Object.entries(_creatureImgMap)) {
  const name = path.split("/").pop().replace(".png", "");
  CREATURE_IMG[name] = url;
}

// Monster sprite assets — static fallback images
import monsterFogo     from "../assets/monsters/fire/fire.png";
import monsterRaio     from "../assets/monsters/electric/electric.png";
import monsterJato     from "../assets/monsters/water/water.png";
import monsterTerra    from "../assets/monsters/earth/earth.png";
import monsterControlo from "../assets/monsters/psychic/psychic.png";

// ── SFX ──────────────────────────────────────────────────────────────────────
import sfxMatch1        from "../assets/audio/sfx/match1.ogg";
import sfxMatch2        from "../assets/audio/sfx/match2.ogg";
import sfxMatch3        from "../assets/audio/sfx/match3.ogg";
import sfxMatch4        from "../assets/audio/sfx/match4.ogg";
import sfxFirePower     from "../assets/audio/sfx/fire_power.ogg";
import sfxWaterPower    from "../assets/audio/sfx/water_power.ogg";
import sfxElecPower     from "../assets/audio/sfx/electric_power.ogg";
import sfxEarthPower    from "../assets/audio/sfx/earth_power.ogg";
import sfxPsychicPower  from "../assets/audio/sfx/psychic_power.ogg";
import sfxVictory       from "../assets/audio/sfx/victory.ogg";
import sfxLooselevel    from "../assets/audio/sfx/looselevel.ogg";
import sfxDuplo         from "../assets/audio/sfx/duplo.ogg";
import sfxTriplo        from "../assets/audio/sfx/triplo.ogg";
import sfxComboVoice    from "../assets/audio/sfx/combo.ogg";
import sfxAttack        from "../assets/audio/sfx/attack.ogg";
import sfxSuperAttack   from "../assets/audio/sfx/super_attack.ogg";
import sfxMegaAttack    from "../assets/audio/sfx/megaattack.ogg";

// ── Level music (region-specific + boss) ─────────────────────────────────────
import musicVolcano from "../assets/audio/music/ignarion_music.ogg";
import musicOcean   from "../assets/audio/music/hydrallis_music.ogg";
import musicEarth   from "../assets/audio/music/terravael_music.ogg";
import musicStorm   from "../assets/audio/music/voltaryn_music.ogg";
import musicPsychic from "../assets/audio/music/aetherion_music.ogg";
import musicBoss    from "../assets/audio/music/boss_fight_music.ogg";

// Animated idle spritesheets (WebP — ~80% smaller than PNG)
import animSheetFogo     from "../assets/monsters/animations/fire/fire_idle.webp";
import animSheetRaio     from "../assets/monsters/animations/electric/electric_idle.webp";
import animSheetJato     from "../assets/monsters/animations/water/water_idle.webp";
import animSheetTerra    from "../assets/monsters/animations/earth/earth_idle.webp";
import animSheetControlo from "../assets/monsters/animations/psychic/psychic_idle.webp";

// ── Beam assets (event strip skill VFX) ──────────────────────────────────────
import beamFire     from "../assets/beams/fire_beam.png";
import beamElectric from "../assets/beams/electric_beam.png";
import beamWater    from "../assets/beams/water_beam.png";
import beamEarth    from "../assets/beams/earth_beam.png";
import beamPsychic  from "../assets/beams/psychic_beam.png";

// Animation frame data (custom flat-array JSON format)
import animJsonFogo     from "../assets/monsters/animations/fire/fire_idle.json";
import animJsonRaio     from "../assets/monsters/animations/electric/electric_idle.json";
import animJsonJato     from "../assets/monsters/animations/water/water_idle.json";
import animJsonTerra    from "../assets/monsters/animations/earth/earth_idle.json";
import animJsonControlo from "../assets/monsters/animations/psychic/psychic_idle.json";

export default class LevelScene extends Phaser.Scene {
  constructor() {
    super("LevelScene");

    // board
    this.rows = 8;
    this.cols = 8;
    this.tileSize = 58;
    this.colors = ["red", "yellow", "green", "blue", "purple"];

    // defaults (substituídos por init())
    this.movesLeft = 20;
    this.goal  = { type: "collect", color: "red", count: 30 };
    this.goal2 = null; // second collect goal (dual-objective levels)

    // XP por nível (acumula durante a run)
    this.xpGains = { fogo: 0, raio: 0, jato: 0, terra: 0, controlo: 0 };
    this.justLeveledUp = []; // [{id, from, to}]

    // runtime state
    this.collected = null;
    this.isBusy = false;
    this.selected = null;

    // skill flow
    this.pendingSkill = null;
    this.armedSkill = null;
    this.previewTiles = [];
    this.previewActive = false;
    this.previewLast = null;
    this.previewJustArmed = false;

    // level identity
    this.regionId = "volcano";
    this.levelId = 1;
    this.levelDef = null;
    this.levelGoals = [];

    // score/combos
    this.score = 0;
    this.maxComboMultiplier = 1;

    // timer (bonus sem penalidade)
    this.timeLeft = 0;
    this.timeBonusEarned = 0;
    this._timerEvent = null;

    // modal
    this.activeModal = null;

    // replay recorder
    this._recorder = new ReplayRecorder();
  }

  // ✅ recebe dados do MapScene
  init(data) {
    this.regionId = data?.regionId || "volcano";
    this.levelId = data?.levelId || 1;

    this.levelDef = LevelStore.getLevel(this.levelId, this.regionId);
    this.levelGoals = this.levelDef?.goals ? [...this.levelDef.goals] : [];

    // reset per-level counters
    this.collected = { red: 0, yellow: 0, green: 0, blue: 0, purple: 0 };
    this.fragmentsEarnedThisLevel = 0;
    this.score = 0;
    this.maxComboMultiplier = 1;
    this._lastCascadeIndex = 0;

    // ✅ reset XP per-level
    this.xpGains = { fogo: 0, raio: 0, jato: 0, terra: 0, controlo: 0 };
    this.justLeveledUp = [];

    // apply moves/goals
    if (this.levelDef) {
      this.movesLeft = this.levelDef.moves ?? 20;

      // goal principal (HUD) = 1º goal
      const g0 = this.levelGoals[0];
      if (g0?.type === "collect") {
        this.goal = { type: "collect", color: g0.color, count: g0.count };
      } else if (g0?.type === "score") {
        this.goal = { type: "score", target: g0.target };
      } else if (g0?.type === "collect_fragments") {
        this.goal = { type: "collect_fragments", count: g0.count };
      } else {
        this.goal = { type: "collect", color: "red", count: 30 };
      }

      // Dual collect goal: second collect in the goals array
      const g1 = this.levelGoals[1];
      this.goal2 = (g0?.type === "collect" && g1?.type === "collect")
        ? { type: "collect", color: g1.color, count: g1.count }
        : null;

      // Score bar: always shown (uses explicit score goal target, or fallback moves×150)
      if (this.goal.type !== "score") {
        const explicitScore = this.levelGoals.find(g => g.type === "score");
        const scoreTarget = explicitScore?.target || Math.max(800, (this.levelDef.moves ?? 18) * 150);
        this.scoreGoal = { type: "score", target: scoreTarget };
      } else {
        this.scoreGoal = null; // primary bar already shows score
      }
    } else {
      this.movesLeft = 20;
      this.goal = { type: "collect", color: "red", count: 30 };
      this.scoreGoal = { type: "score", target: 800 };
    }

    // reset transient
    this.isBusy = false;
    this.selected = null;

    this.pendingSkill = null;
    this.armedSkill = null;
    this.previewTiles = [];
    this.previewActive = false;
    this.previewLast = null;
    this.previewJustArmed = false;

    this.activeModal = null;

    // ── Modo Infinito ─────────────────────────────────────────────────────
    this.infinite = !!data?.infinite;
    if (this.infinite) {
      this.regionId   = "psychic"; // tema visual
      this.levelId    = 0;
      this.levelDef   = null;
      this.levelGoals = [];
      this.movesLeft  = 9999;
      this.goal       = null;
      this.scoreGoal  = null;
      this._infiniteWave         = 1;
      this._infiniteMatchCount   = 0; // moves feitos com sucesso
    }
  }

  // mapping: monstro -> cor exigida no "maior grupo" da próxima jogada
  getRequiredColorForMonster(monsterId) {
    const map = {
      fogo: "red",
      raio: "yellow",
      jato: "blue",
      terra: "green",
      controlo: "purple",
    };
    return map[monsterId] || null;
  }

  // ✅ cor -> monsterId (para XP)
  colorToMonsterId(color) {
    const map = {
      red: "fogo",
      yellow: "raio",
      blue: "jato",
      green: "terra",
      purple: "controlo",
    };
    return map[color] || null;
  }

  // ✅ XP por tiles removidos + bónus por combo
  addXpForClearedTiles(clearedByColor, comboMultiplier) {
    for (const [color, n] of Object.entries(clearedByColor || {})) {
      const id = this.colorToMonsterId(color);
      if (!id) continue;

      const base = Number(n || 0);
      const bonus = comboMultiplier >= 2 ? (comboMultiplier - 1) * 2 : 0;

      this.xpGains[id] = (this.xpGains[id] || 0) + base + bonus;
    }
  }

  // ✅ XP direto num monstro (usado pela SKILL)
  addXpToMonster(monsterId, amount, comboMultiplier = 1) {
    const base = Math.max(0, Number(amount || 0));
    const bonus = comboMultiplier >= 2 ? (comboMultiplier - 1) * 2 : 0;
    if (!this.xpGains[monsterId]) this.xpGains[monsterId] = 0;
    this.xpGains[monsterId] += base + bonus;
  }

  preload() {
    // ── SFX ──────────────────────────────────────────────────────────────────
    this.load.audio('sfx_match1',         sfxMatch1);
    this.load.audio('sfx_match2',         sfxMatch2);
    this.load.audio('sfx_match3',         sfxMatch3);
    this.load.audio('sfx_match4',         sfxMatch4);
    this.load.audio('sfx_fire_power',     sfxFirePower);
    this.load.audio('sfx_water_power',    sfxWaterPower);
    this.load.audio('sfx_electric_power', sfxElecPower);
    this.load.audio('sfx_earth_power',    sfxEarthPower);
    this.load.audio('sfx_psychic_power',  sfxPsychicPower);
    this.load.audio('sfx_victory',        sfxVictory);
    this.load.audio('sfx_looselevel',     sfxLooselevel);
    this.load.audio('sfx_duplo',          sfxDuplo);
    this.load.audio('sfx_triplo',         sfxTriplo);
    this.load.audio('sfx_combo',          sfxComboVoice);
    this.load.audio('sfx_attack',         sfxAttack);
    this.load.audio('sfx_super_attack',   sfxSuperAttack);
    this.load.audio('sfx_megaattack',     sfxMegaAttack);

    // ── Reward creature sprite (para o modal de descoberta) ──────────────────
    const _rewardCId = this.levelDef?.reward?.creatureId;
    if (_rewardCId && CREATURE_IMG[_rewardCId]) {
      this.load.image(`creature-${_rewardCId}`, CREATURE_IMG[_rewardCId]);
    }

    // ── Level music — carrega só o ficheiro necessário ────────────────────────
    const _REGION_MUSIC = {
      volcano: musicVolcano,
      ocean:   musicOcean,
      earth:   musicEarth,
      storm:   musicStorm,
      psychic: musicPsychic,
    };
    const _isBoss = this.levelDef?.boss?.enabled === true;
    const _allTracks = Object.values(_REGION_MUSIC);
    const _shuffledTrack = _allTracks[Math.floor(Math.random() * _allTracks.length)];
    const _chosenTrack = Settings.musicShuffle ? _shuffledTrack : (_REGION_MUSIC[this.regionId] || musicVolcano);
    this.load.audio("bgm_level", _isBoss ? musicBoss : _chosenTrack);

    // ── Region background image (one per play session) ────────────────────
    const _REGION_BG = {
      volcano: ['bg-ignarion',  bgIgnarion],
      ocean:   ['bg-hydrallis', bgHydrallis],
      earth:   ['bg-terraevel', bgTerraevel],
      storm:   ['bg-voltrix',   bgVoltrix],
      psychic: ['bg-aetherion', bgAetherion],
    };
    const _bgEntry = _REGION_BG[this.regionId];
    if (_bgEntry) this.load.image(_bgEntry[0], _bgEntry[1]);

    this.load.image("level-villain", villainImg);

    // ── Beam VFX (event strip skill animations) ──────────────────────────────
    this.load.image("beam-fogo",     beamFire);
    this.load.image("beam-raio",     beamElectric);
    this.load.image("beam-jato",     beamWater);
    this.load.image("beam-terra",    beamEarth);
    this.load.image("beam-controlo", beamPsychic);

    // Static fallback images (used if animation sheet fails to register)
    this.load.image("monster-fogo",     monsterFogo);
    this.load.image("monster-raio",     monsterRaio);
    this.load.image("monster-jato",     monsterJato);
    this.load.image("monster-terra",    monsterTerra);
    this.load.image("monster-controlo", monsterControlo);

    // Animated idle spritesheets
    this.load.image("anim-sheet-fogo",     animSheetFogo);
    this.load.image("anim-sheet-raio",     animSheetRaio);
    this.load.image("anim-sheet-jato",     animSheetJato);
    this.load.image("anim-sheet-terra",    animSheetTerra);
    this.load.image("anim-sheet-controlo", animSheetControlo);
  }

  // ── Registers per-frame custom atlas + Phaser animation keys for all 5 monsters ──
  _registerMonsterAnims() {
    const animSets = [
      { id: "fogo",     frames: animJsonFogo     },
      { id: "raio",     frames: animJsonRaio     },
      { id: "jato",     frames: animJsonJato     },
      { id: "terra",    frames: animJsonTerra    },
      { id: "controlo", frames: animJsonControlo },
    ];

    for (const { id, frames } of animSets) {
      const sheetKey = `anim-sheet-${id}`;
      if (!this.textures.exists(sheetKey) || !Array.isArray(frames) || !frames.length) continue;

      const tex = this.textures.get(sheetKey);

      // Register each frame individually — sizes vary slightly so addSpriteSheet can't be used
      for (const f of frames) {
        tex.add(f.frameIndex, 0, f.x, f.y, f.width, f.height);
      }

      // Create looping idle animation
      this.anims.create({
        key: `idle-${id}`,
        frames: frames.map(f => ({ key: sheetKey, frame: f.frameIndex })),
        frameRate: 8,
        repeat: -1,
      });
    }
  }

  create() {
    const boardW = this.cols * this.tileSize;
    const boardH = this.rows * this.tileSize;
    this.boardX = (this.scale.width - boardW) / 2;
    this.boardY = 210;  // 180 + 30px safe-area for iPhone notch

    // Register animated idle spritesheets before anything else draws
    this._registerMonsterAnims();

    // ── Background (depth 0-5, drawn first so everything sits on top) ──────
    const _bgAnalyserRef = { current: null };
    const { boardFrame, triggerBurst, bgUpdate } = createBackground(this, this.regionId, {
      boardX:         this.boardX,
      boardY:         this.boardY,
      boardW,
      boardH,
      analyserGetter: () => _bgAnalyserRef.current,
    });
    this._boardFrame    = boardFrame;
    this._bgBurst       = triggerBurst;
    this._bgUpdate      = bgUpdate;
    this._bgAnalyserRef = _bgAnalyserRef;

    // ===== XP SYSTEM (carregado do localStorage) =====
    this.playerLevel  = LevelStore.getTrainerLevel();
    this.playerXP     = LevelStore.getTrainerXP();
    this.nextLevelXP  = LevelStore.getTrainerNextXP();
    this._statusTimer = null; // referência ao timer de limpeza do status

    // HUD
    this.hud = new HUD(this, { type: this.levelDef?.hudType || 'normal' });
    this.hud.setLevel(this.playerLevel, this.playerXP, this.nextLevelXP);
    this.hud.setFragments?.();   // inicializa com valor atual do localStorage
    this.hud.setScoreColor((RegionThemes[this.regionId] || RegionThemes.volcano).primary);

    if (this.infinite) {
      this.hud.setMoves("∞");
      this.hud.setGoal(null, 0);
      this.hud.setScoreBar(null);
      // 3-minute countdown
      this.timeLeft = 180;
      this.hud.setTimer(this.timeLeft);
      this._startTimer();
    } else {
      this.hud.setMoves(this.movesLeft);
      if (this.goal?.type === "collect") {
        this.hud.setGoal(this.goal, this.collected[this.goal.color] || 0,
          this.goal2, this.goal2 ? (this.collected[this.goal2.color] || 0) : undefined);
      } else if (this.goal?.type === "collect_fragments") {
        this.hud.setGoal(this.goal, this.fragmentsEarnedThisLevel);
      } else {
        this.hud.setStatus(I18n.t('level_status', { n: this.levelId, type: this.goal.type.toUpperCase() }));
      }
      this.hud.setScoreBar(this.scoreGoal);

      // Timer de bonus (só existe se o nível definir timeLimit)
      if (this.levelDef?.timeLimit) {
        this.timeLeft = this.levelDef.timeLimit;
        this.hud.setTimer(this.timeLeft);
        this._startTimer();
      }
    }

    // Board
    this.board = new Board(this, {
      rows: this.rows,
      cols: this.cols,
      tileSize: this.tileSize,
      x: this.boardX,
      y: this.boardY,
      colors: this.colors,
    });

    this.board.generateWithoutInitialMatches();
    if (!hasPossibleMoves(this.board.grid)) shuffleBoard(this.board);

    // ── Replay Recorder — snapshot initial board ────────────────────────
    this._recorder.reset();
    this._recorder.startLevel({
      levelId: this.levelId,
      regionId: this.regionId,
      movesLeft: this.movesLeft,
      goals: this.levelGoals,
      board: this.board,
      infinite: this.infinite,
    });

    // MonsterBar
    this.monsterBar = new MonsterBar(
      this,
      [
        { id: "fogo",     name: "Pyron",   element: "fire",     energyRequired: 18 },
        { id: "raio",     name: "Voltrix", element: "electric", energyRequired: 18 },
        { id: "jato",     name: "Aquyra",  element: "water",    energyRequired: 14 },
        { id: "terra",    name: "Sylrana", element: "earth",    energyRequired: 22 },
        { id: "controlo", name: "Athena",  element: "psychic",  energyRequired: 16 },
      ],
      { onUse: (monster) => this.requestUseSkill(monster) }
    );

    this._startIdleAnims();

    // ── Mood Engine ───────────────────────────────────────────────────────────
    this.moodPresenter  = new MoodPresenter(this);
    this.moodEngine     = new MoodEngine(this, this.monsterBar);
    this._idleCheckTimer = this.time.addEvent({
      delay:         3000,
      loop:          true,
      callback:      () => this.moodEngine.checkIdle(this._moodState()),
      callbackScope: this,
    });

    this._createCancelButton();

    // ── Event Strip ──────────────────────────────────────────────────────────
    // Sits in the dead gap between board bottom (boardY + rows*tileSize) and monster bar
    const gapY = this.boardY + this.rows * this.tileSize + 73;
    this.eventText = this.add.text(this.scale.width / 2, gapY, "", {
      fontFamily: "system-ui, Arial",
      fontSize: "22px",
      color: "#e2e8f0",
      stroke: "#0b0f14",
      strokeThickness: 4,
    }).setOrigin(0.5).setAlpha(0).setDepth(200);

    // Input tiles — drag-to-swap (mobile) + tap-tap (desktop)
    this._drag = null;

    this.input.on("gameobjectdown", (_pointer, gameObject) => {
      if (this.activeModal) return;
      const tileData = gameObject.__tileData;
      if (!tileData) return;
      this._drag = { r: tileData.r, c: tileData.c, x: _pointer.x, y: _pointer.y, isDrag: false };
    });

    this.input.on("pointerup", async () => {
      if (!this._drag) return;
      const drag = this._drag;
      this._drag = null;
      if (drag.isDrag) {
        await this.handleTileSwap(drag.r, drag.c, drag.dr, drag.dc);
      } else {
        await this.handleTileTap(drag.r, drag.c);
      }
    });

    // Preview + drag detection
    this.input.on("pointermove", (pointer) => {
      if (this.activeModal) return;

      // Drag detection — fires before tile is released
      if (this._drag && !this._drag.isDrag) {
        const dx = pointer.x - this._drag.x;
        const dy = pointer.y - this._drag.y;
        if (Math.abs(dx) > 15 || Math.abs(dy) > 15) {
          this._drag.isDrag = true;
          if (this.selected) {
            this.board.highlight(this.selected.r, this.selected.c, false);
            this.selected = null;
          }
          const horizontal = Math.abs(dx) >= Math.abs(dy);
          this._drag.dr = horizontal ? 0 : (dy > 0 ? 1 : -1);
          this._drag.dc = horizontal ? (dx > 0 ? 1 : -1) : 0;
        }
      }

      if (!this.previewActive) return;
      const pos = this.worldToTile(pointer.worldX, pointer.worldY);
      if (!pos) return;

      if (this.previewLast && this.previewLast.r === pos.r && this.previewLast.c === pos.c) return;
      this.previewLast = pos;
      this.showPreview(pos.r, pos.c);
    });

    // Cancel preview ao clicar fora
    this.input.on("pointerdown", (pointer) => {
      if (this.activeModal) return;
      if (!this.previewActive) return;
      if (this.previewJustArmed) return;

      const pos = this.worldToTile(pointer.worldX, pointer.worldY);
      if (!pos) this.cancelSkillPreview();
    });

    // ESC
    this.input.keyboard?.on("keydown-ESC", () => {
      if (this.activeModal) {
        this.closeModal();
        return;
      }

      if (this.previewActive) {
        this.cancelSkillPreview();
      } else if (this.armedSkill) {
        const id = this.armedSkill.monsterId;
        this.armedSkill = null;
        this.hud.setStatus("");
        this.onMonsterIdle(id);
        this._hideCancelButton();
      } else {
        this.scene.start("MapScene");
      }
    });

    // ✅ Modal pré-nível
    this.cameras.main.fadeIn(350, 0, 0, 0);
    if (this.infinite) {
      this.showInfiniteIntroModal();
    } else {
      this.showLevelIntroModal();
    }

    // 🔊 Música do nível em loop — stop any lingering audio first (title music
    //    can leak on mobile when the AudioContext unlocks after scene transition)
    this.sound.stopAll();
    this.bgm = this.sound.add("bgm_level", { loop: true, volume: Settings.musicVolume });
    this.bgm.play();
    this.events.once("shutdown", () => this.bgm?.stop());

    // Music analyser for equalizer bars
    try {
      const _sound = this.sound.get('bgm_level');
      if (_sound?.gainNode && this.sound.context) {
        const _analyser = this.sound.context.createAnalyser();
        _analyser.fftSize = 64;
        _sound.gainNode.connect(_analyser);
        _analyser.connect(this.sound.context.destination);
        this._bgAnalyserRef.current = _analyser;
      }
    } catch (_) { /* Web Audio unavailable */ }

    // Drive per-frame background VFX (music bars + psychic orbit)
    this.events.on('update', () => this._bgUpdate?.());
  }

  // -----------------------------
  // MODALS
  // -----------------------------
  setGameplayEnabled(_enabled) {
    // NÃO desligar this.input.enabled,
    // porque isso desativa também os botões do modal.
    // O bloqueio do gameplay já é feito por: if (this.activeModal) return;
  }

  showLevelIntroModal() {
    const theme      = RegionThemes[this.regionId] || RegionThemes.volcano;
    const W          = this.scale.width;
    const H          = this.scale.height;
    const D          = 1000;
    const regionName = (theme.name || this.regionId).toUpperCase();
    const levelLabel = `${I18n.t('level_label')} ${this.levelId}${this.levelDef?.name ? '  —  ' + this.levelDef.name.toUpperCase() : ''}`;
    const introText  = this.levelDef?.ui?.intro || '';
    // Strip the "Objetivos:" header — we already show the OBJETIVO Kamikaze label
    const goalsText  = this.formatGoalsText(this.levelGoals).split('\n').slice(1).join('\n');

    const objs = [];
    const push = o => { objs.push(o); return o; };
    let dismissed = false;
    let autoTimer  = null;

    const dismiss = (goToMap = false) => {
      if (dismissed) return;
      dismissed = true;
      if (autoTimer) { autoTimer.remove(); autoTimer = null; }
      for (const o of objs) this.tweens.killTweensOf(o);
      if (goToMap) {
        for (const o of objs) o?.destroy?.();
        this.setGameplayEnabled(false);
        this.scene.start('MapScene');
        return;
      }
      this.tweens.add({
        targets: objs.filter(o => o?.active),
        alpha: 0,
        duration: 280,
        ease: 'Quad.Out',
        onComplete: () => {
          for (const o of objs) o?.destroy?.();
          this.activeModal = null;
          this.setGameplayEnabled(true);
        },
      });
    };

    // ── Dark overlay (tap to skip) ─────────────────────────────────────────
    const bg = push(this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 1)
      .setAlpha(0).setDepth(D).setInteractive());
    this.tweens.add({ targets: bg, alpha: 0.93, duration: 350 });
    bg.on('pointerdown', () => dismiss());

    // ── Region name ────────────────────────────────────────────────────────
    const regionTxt = push(this.add.text(W / 2, H * 0.19, regionName, {
      fontFamily: 'Kamikaze, Arial',
      fontSize:   '52px',
      color:      theme.secondary,
      stroke:     '#000000',
      strokeThickness: 5,
    }).setOrigin(0.5).setAlpha(0).setDepth(D + 1));

    this.time.delayedCall(80, () => {
      regionTxt.setY(H * 0.10);
      this.tweens.add({ targets: regionTxt, y: H * 0.19, alpha: 1, duration: 420, ease: 'Back.Out(1.4)' });
    });

    // ── Level label ────────────────────────────────────────────────────────
    const levelTxt = push(this.add.text(W / 2, H * 0.30, levelLabel, {
      fontFamily: 'system-ui, Arial',
      fontSize:   '18px',
      color:      '#94a3b8',
      letterSpacing: 2,
    }).setOrigin(0.5).setAlpha(0).setDepth(D + 1));
    this.time.delayedCall(320, () =>
      this.tweens.add({ targets: levelTxt, alpha: 1, duration: 350 }));

    // ── Accent divider ─────────────────────────────────────────────────────
    const divider = push(this.add.rectangle(W / 2, H * 0.36, 0, 2,
      hexToPhaser(theme.primary), 0.8).setDepth(D + 1));
    this.time.delayedCall(480, () =>
      this.tweens.add({ targets: divider, width: W * 0.55, duration: 380, ease: 'Quad.Out' }));

    // ── Intro flavour text ─────────────────────────────────────────────────
    if (introText) {
      const intro = push(this.add.text(W / 2, H * 0.43, `"${introText}"`, {
        fontFamily:  'system-ui, Arial',
        fontSize:    '19px',
        color:       '#e2e8f0',
        fontStyle:   'italic',
        align:       'center',
        wordWrap:    { width: W - 70 },
      }).setOrigin(0.5).setAlpha(0).setDepth(D + 1));
      this.time.delayedCall(680, () =>
        this.tweens.add({ targets: intro, alpha: 1, duration: 380 }));
    }

    // ── Objectives ─────────────────────────────────────────────────────────
    const objY = introText ? H * 0.50 : H * 0.42;

    const objLabel = push(this.add.text(W / 2, objY, I18n.t('objective'), {
      fontFamily: 'Kamikaze, Arial',
      fontSize:   '26px',
      color:      theme.primary,
    }).setOrigin(0.5).setAlpha(0).setDepth(D + 1));

    const objTxt = push(this.add.text(W / 2, objY + 42, goalsText, {
      fontFamily:  'system-ui, Arial',
      fontSize:    '22px',
      color:       '#e2e8f0',
      align:       'center',
      wordWrap:    { width: W - 60 },
      lineSpacing: 12,
    }).setOrigin(0.5).setAlpha(0).setDepth(D + 1));

    const movesTxt = push(this.add.text(W / 2, objY + 100, I18n.t('moves_label', { n: this.movesLeft }), {
      fontFamily: 'system-ui, Arial',
      fontSize:   '18px',
      color:      '#94a3b8',
    }).setOrigin(0.5).setAlpha(0).setDepth(D + 1));

    this.time.delayedCall(980, () =>
      this.tweens.add({ targets: [objLabel, objTxt, movesTxt], alpha: 1, duration: 380 }));

    // ── MAPA button ────────────────────────────────────────────────────────
    const mapaBtn = push(this.add.text(28, H - 32, I18n.t('btn_map'), {
      fontFamily: 'system-ui, Arial',
      fontSize:   '13px',
      color:      '#475569',
    }).setOrigin(0, 1).setAlpha(0).setDepth(D + 2).setInteractive());
    mapaBtn.on('pointerdown', () => dismiss(true));
    mapaBtn.on('pointerover',  () => mapaBtn.setColor('#94a3b8'));
    mapaBtn.on('pointerout',   () => mapaBtn.setColor('#475569'));
    this.time.delayedCall(700, () =>
      this.tweens.add({ targets: mapaBtn, alpha: 1, duration: 300 }));

    // ── "tap to start" blinking hint ───────────────────────────────────────
    const hint = push(this.add.text(W / 2, H - 32, I18n.t('tap_start'), {
      fontFamily: 'system-ui, Arial',
      fontSize:   '11px',
      color:      '#475569',
    }).setOrigin(0.5, 1).setAlpha(0).setDepth(D + 2));
    this.time.delayedCall(1400, () =>
      this.tweens.add({ targets: hint, alpha: { from: 0, to: 0.65 }, duration: 600, yoyo: true, repeat: -1 }));

    // ── Auto-advance at 2.5s ───────────────────────────────────────────────
    autoTimer = this.time.delayedCall(6500, () => dismiss());

    this.activeModal = { destroy: () => dismiss() };
    this.setGameplayEnabled(false);
  }

  // ── Win modal ──────────────────────────────────────────────────────────────
  showWinModal(stars) {
    const theme   = RegionThemes[this.regionId] || RegionThemes.volcano;
    const primary = hexToPhaser(theme.primary);
    const W = this.scale.width, H = this.scale.height, D = 1000;

    const evoLines = [];
    if (Array.isArray(this.justLeveledUp)) {
      for (const e of this.justLeveledUp) {
        const label = e.to === 2 ? "SUPER ATTACK x2" : e.to === 3 ? "MEGA ATTACK x3" : "";
        evoLines.push(`✦ ${String(e.id).toUpperCase()} Lv${e.from}→Lv${e.to}${label ? "  " + label : ""}`);
      }
    }

    const bodyLines = [
      this.formatGoalsText(this.levelGoals),
      "",
      `${I18n.t('score_prefix')} ${this.score - this.timeBonusEarned}    ${I18n.t('combo_max', { n: this.maxComboMultiplier })}`,
      ...( this.timeBonusEarned > 0
        ? [I18n.t('time_bonus', { n: this.timeBonusEarned, t: this.timeLeft })]
        : [] ),
      ...( this.timeBonusEarned > 0 ? [`Total: ${this.score}`] : [] ),
      ...( evoLines.length ? ["", ...evoLines] : [] ),
    ];

    const nextId = this.levelId + 1;
    const hasNext = !!LevelStore.getLevel(nextId, this.regionId);

    const c = this.createModalContainer({
      title: `${"★".repeat(stars)}${"☆".repeat(3 - stars)}  ${I18n.t('victory')}`,
      titleColor: theme.secondary,
      accentColor: theme.primary,
      bodyLines,
      buttons: [
        hasNext ? {
          label: I18n.t('next_btn'),
          primary: true,
          onClick: () => { this.closeModal(true); this.scene.start("LevelScene", { regionId: this.regionId, levelId: nextId }); },
        } : {
          label: I18n.t('map_btn_plain'),
          primary: true,
          onClick: () => { this.closeModal(true); this.scene.start("MapScene", { regionId: this.regionId, fromVictory: true }); },
        },
        // Segundo botão "MAPA" só aparece quando há próximo nível (alternativa ao PRÓXIMO →)
        ...(hasNext ? [{
          label: I18n.t('map_btn_plain'),
          onClick: () => { this.closeModal(true); this.scene.start("MapScene", { regionId: this.regionId, fromVictory: true }); },
        }] : []),
      ],
    });
    this.activeModal = c;
    this.setGameplayEnabled(false);
  }

  // ── Defeat modal ───────────────────────────────────────────────────────────
  showDefeatModal() {
    const W = this.scale.width, H = this.scale.height, D = 1000;
    const objs = [];

    const push = (o) => { objs.push(o); return o; };

    // Dark overlay
    push(this.add.rectangle(W/2, H/2, W, H, 0x000000, 0.82).setDepth(D));

    // Villain ghost behind panel
    if (this.textures.exists("level-villain")) {
      push(this.add.image(W/2, H*0.42, "level-villain")
        .setDisplaySize(W * 0.75, W * 0.75)
        .setAlpha(0.14)
        .setDepth(D + 1)
        .setBlendMode(Phaser.BlendModes.MULTIPLY));
    }

    // Panel — dark red tinge
    const panelH = 340;
    push(this.add.rectangle(W/2, H/2, W - 40, panelH, 0x130608, 0.97)
      .setStrokeStyle(2, 0xef4444, 0.7)
      .setDepth(D + 2));

    // Top accent line
    push(this.add.rectangle(20, H/2 - panelH/2, W - 40, 3, 0xef4444, 0.6)
      .setOrigin(0, 0).setDepth(D + 3));

    // Title
    push(this.add.text(W/2, H/2 - panelH/2 + 22, I18n.t('defeat'), {
      fontFamily: "system-ui, Arial", fontSize: "24px",
      color: "#ef4444", fontStyle: "bold",
    }).setOrigin(0.5, 0).setDepth(D + 3));

    // Villain taunt
    const TAUNTS = I18n.taunts();
    const taunt = TAUNTS[Math.floor(Math.random() * TAUNTS.length)];
    push(this.add.text(W/2, H/2 - panelH/2 + 58, `"${taunt}"`, {
      fontFamily: "system-ui, Arial", fontSize: "13px",
      color: "#94a3b8", fontStyle: "italic",
      wordWrap: { width: W - 80 }, align: "center",
    }).setOrigin(0.5, 0).setDepth(D + 3));

    // Goals progress
    push(this.add.text(W/2, H/2 - panelH/2 + 105, this.formatGoalsText(this.levelGoals), {
      fontFamily: "system-ui, Arial", fontSize: "13px",
      color: "#cbd5e1", lineSpacing: 5,
    }).setOrigin(0.5, 0).setDepth(D + 3));

    push(this.add.text(W/2, H/2 + 55, `${I18n.t('score_prefix')} ${this.score}`, {
      fontFamily: "system-ui, Arial", fontSize: "13px", color: "#64748b",
    }).setOrigin(0.5).setDepth(D + 3));

    // Buttons
    const btnY = H/2 + panelH/2 - 52;
    const btnW = (W - 40 - 36 - 12) / 2;

    const mkBtn = (bx, label, primary, onClick) => {
      const r = push(this.add.rectangle(bx, btnY, btnW, 40,
        primary ? 0x7f1d1d : 0x1f2937, 1)
        .setStrokeStyle(2, primary ? 0xef4444 : 0x334155, 1)
        .setDepth(D + 3).setInteractive({ useHandCursor: true }));
      push(this.add.text(bx, btnY, label, {
        fontFamily: "system-ui, Arial", fontSize: "14px", color: "#e2e8f0",
      }).setOrigin(0.5).setDepth(D + 4));
      r.on("pointerdown", () => {
        this.tweens.add({ targets: r, scale: 0.97, yoyo: true, duration: 70 });
        onClick();
      });
    };

    const lx = W/2 - (W - 40)/2 + 18 + btnW/2;
    const rx = lx + btnW + 12;
    mkBtn(lx, I18n.t('retry'),         true,  () => { this._destroyModal(objs); this.scene.start("LevelScene", { regionId: this.regionId, levelId: this.levelId }); });
    mkBtn(rx, I18n.t('map_btn_plain'), false, () => { this._destroyModal(objs); this.scene.start("MapScene"); });

    this.activeModal = { destroy: () => this._destroyModal(objs) };
    this.setGameplayEnabled(false);
  }

  // ── Infinite Mode — intro, wave helpers, game-over ───────────────────────

  showInfiniteIntroModal() {
    const best = LevelStore.getInfiniteBestScore();
    const bestLine = best > 0 ? I18n.t('best', { n: best.toLocaleString() }) : I18n.t('no_record');

    const c = this.createModalContainer({
      title:       I18n.t('infinite_title'),
      titleColor:  "#d084fc",
      accentColor: "#7b1fa2",
      bodyLines: [
        I18n.t('infinite_no_goals'),
        I18n.t('infinite_time'),
        "",
        I18n.t('infinite_mult'),
        "",
        bestLine,
      ],
      buttons: [
        { label: I18n.t('play_btn'),       primary: true, onClick: () => this.closeModal() },
        { label: I18n.t('map_btn_plain'),               onClick: () => { this.closeModal(true); this.scene.start("MapScene"); } },
      ],
    });
    this.activeModal = c;
    this.setGameplayEnabled(false);
  }

  _getWaveMultiplier() {
    // Wave 1 = ×1.0, each wave +0.15, cap at ×4.0
    return Math.min(4.0, 1 + (this._infiniteWave - 1) * 0.15);
  }

  _checkInfiniteWave() {
    if (!this.infinite) return;
    this._infiniteMatchCount++;
    if (this._infiniteMatchCount % 15 === 0) {
      this._infiniteWave++;
      const mult = this._getWaveMultiplier().toFixed(1);
      this._setStatus(I18n.t('wave_status', { n: this._infiniteWave, mult }), 2500);
    }
  }

  onInfiniteGameOver() {
    if (this.activeModal) return; // already showing
    this._stopTimer();
    this._fadeOutBgm(0.1, 800);

    // ── Replay: record infinite game over ─────────────────────────────
    this._recorder.recordInfiniteEnd({
      score: this.score,
      wave: this._infiniteWave,
      maxCombo: this.maxComboMultiplier,
    });

    const isRecord = LevelStore.saveInfiniteScore(this.score, this._infiniteWave);
    const best     = LevelStore.getInfiniteBestScore();

    const W = this.scale.width, H = this.scale.height, D = 1000;
    const objs = [];
    const push = (o) => { objs.push(o); return o; };

    const hasClip = this._recorder.hasReplay();
    const panelH = hasClip ? 370 : 320;
    push(this.add.rectangle(W/2, H/2, W - 30, panelH, 0x0a0014, 0.96)
      .setStrokeStyle(2, 0x7b1fa2, 0.9).setDepth(D + 2));

    push(this.add.rectangle(20, H/2 - panelH/2, W - 40, 3, 0xd084fc, 0.7)
      .setOrigin(0, 0).setDepth(D + 3));

    push(this.add.text(W/2, H/2 - panelH/2 + 22, I18n.t('time_up'), {
      fontFamily: "system-ui, Arial", fontSize: "22px",
      color: "#d084fc", fontStyle: "bold",
    }).setOrigin(0.5, 0).setDepth(D + 3));

    push(this.add.text(W/2, H/2 - panelH/2 + 68, I18n.t('final_score'), {
      fontFamily: "system-ui, Arial", fontSize: "13px", color: "#94a3b8",
    }).setOrigin(0.5, 0).setDepth(D + 3));

    push(this.add.text(W/2, H/2 - panelH/2 + 90, this.score.toLocaleString(), {
      fontFamily: "Kamikaze, system-ui, Arial", fontSize: "42px", color: "#f0abfc",
    }).setOrigin(0.5, 0).setDepth(D + 3));

    const recordLine = isRecord ? I18n.t('new_record') : I18n.t('best', { n: best.toLocaleString() });
    push(this.add.text(W/2, H/2 - panelH/2 + 148, recordLine, {
      fontFamily: "system-ui, Arial", fontSize: "14px",
      color: isRecord ? "#fde68a" : "#64748b",
    }).setOrigin(0.5, 0).setDepth(D + 3));

    push(this.add.text(W/2, H/2 - panelH/2 + 180, I18n.t('max_wave', { n: this._infiniteWave }), {
      fontFamily: "system-ui, Arial", fontSize: "12px", color: "#64748b",
    }).setOrigin(0.5, 0).setDepth(D + 3));

    const btnY = H/2 + panelH/2 - 52;
    const btnW = (W - 40 - 36 - 12) / 2;
    const mkBtn = (bx, label, primary, onClick) => {
      const r = push(this.add.rectangle(bx, btnY, btnW, 40,
        primary ? 0x2e0652 : 0x1f2937, 1)
        .setStrokeStyle(2, primary ? 0xd084fc : 0x334155, 1)
        .setDepth(D + 3).setInteractive({ useHandCursor: true }));
      push(this.add.text(bx, btnY, label, {
        fontFamily: "system-ui, Arial", fontSize: "14px", color: "#e2e8f0",
      }).setOrigin(0.5).setDepth(D + 4));
      r.on("pointerdown", () => {
        this.tweens.add({ targets: r, scale: 0.97, yoyo: true, duration: 70 });
        onClick();
      });
    };

    const lx = W/2 - (W - 40)/2 + 18 + btnW/2;
    const rx = lx + btnW + 12;
    mkBtn(lx, I18n.t('play_again'),    true,  () => { this._destroyModal(objs); this.scene.start("LevelScene", { infinite: true }); });
    mkBtn(rx, I18n.t('map_btn_plain'), false, () => { this._destroyModal(objs); this.scene.start("MapScene"); });

    // ── Replay clip button (infinite mode) ──────────────────────────────
    if (this._recorder.hasReplay()) {
      const clipY = btnY + 52;
      const clipBg = push(this.add.rectangle(W/2, clipY, btnW * 2 + 12, 36, 0x1a0a2e, 1)
        .setStrokeStyle(1.5, 0xfbbf24, 0.8)
        .setDepth(D + 3).setInteractive({ useHandCursor: true }));
      push(this.add.text(W/2, clipY, I18n.t('replay_btn'), {
        fontFamily: "Kamikaze, system-ui, Arial", fontSize: "14px", color: "#fbbf24",
      }).setOrigin(0.5).setDepth(D + 4));
      clipBg.on("pointerdown", () => {
        this.tweens.add({ targets: clipBg, scale: 0.97, yoyo: true, duration: 70 });
        this._destroyModal(objs);
        this.cameras.main.fadeOut(175, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start('ReplayScene', {
            replayData: this._recorder.getReplayData(),
            returnTo: 'MapScene',
            returnData: {},
          });
        });
      });
    }

    this.activeModal = { destroy: () => this._destroyModal(objs) };
    this.setGameplayEnabled(false);
  }

  // ── Reward reveal modal — 3 phases ─────────────────────────────────────────
  showRewardRevealModal(reward, stars) {
    const W = this.scale.width, H = this.scale.height, D = 1000;

    // Map creature type → theme colours
    const typeToRegion = { fire: "volcano", water: "ocean", earth: "earth", electric: "storm", psychic: "psychic" };
    const revealTheme  = RegionThemes[typeToRegion[reward.type] || "volcano"];
    const primary      = revealTheme.primary;
    const primaryInt   = hexToPhaser(primary);

    const suspenseText = I18n.getSuspense(reward.type);

    // ── PHASE 1 : SUSPENSE ───────────────────────────────────────────────────
    const p1 = [];
    const ppush = (o) => { p1.push(o); return o; };

    ppush(this.add.rectangle(W/2, H/2, W, H, 0x000000, 0.95).setDepth(D));

    // Silhouette orb — pulsing dark circle with "?"
    const siloOrb = ppush(this.add.circle(W/2, H*0.42, 62, 0x1a1a1a, 1)
      .setStrokeStyle(3, 0x2d2d2d, 0.9).setDepth(D + 2));
    const siloQ = ppush(this.add.text(W/2, H*0.42, "?", {
      fontFamily: "system-ui, Arial", fontSize: "52px",
      color: "#2d2d2d", fontStyle: "bold",
    }).setOrigin(0.5).setDepth(D + 3));

    this.tweens.add({ targets: [siloOrb, siloQ], scaleX: 1.12, scaleY: 1.12, duration: 800, yoyo: true, repeat: -1, ease: "Sine.InOut" });

    const suspTxt = ppush(this.add.text(W/2, H*0.62, suspenseText, {
      fontFamily: "system-ui, Arial", fontSize: "15px",
      color: "#64748b", fontStyle: "italic",
    }).setOrigin(0.5).setAlpha(0).setDepth(D + 2));

    this.tweens.add({ targets: suspTxt, alpha: 1, duration: 700, delay: 400 });

    const tapTxt = ppush(this.add.text(W/2, H*0.78, I18n.t('tap_reveal'), {
      fontFamily: "system-ui, Arial", fontSize: "13px", color: "#334155",
    }).setOrigin(0.5).setAlpha(0).setDepth(D + 2));
    this.time.delayedCall(2000, () => { this.tweens.add({ targets: tapTxt, alpha: 1, duration: 500 }); });

    const triggerReveal = () => {
      p1.forEach(o => o?.destroy?.());

      // ── PHASE 2 : FLASH ────────────────────────────────────────────────────
      const flash = this.add.rectangle(W/2, H/2, W, H, 0xffffff, 1).setDepth(D + 10);
      // 🔊 SFX HOOK: 'sfx_reveal_flash' — bright flash crack sound
      // this.sound?.play?.('sfx_reveal_flash', { volume: 0.9 });

      this.tweens.add({
        targets: flash, alpha: 0, duration: 380, ease: "Quad.Out",
        onComplete: () => { flash.destroy(); this._rewardPhase3(reward, stars, revealTheme, primaryInt, primary, W, H, D); },
      });
    };

    // Tap or auto-advance after 4.5s
    p1[0].setInteractive().once("pointerdown", triggerReveal);
    this.time.delayedCall(4500, () => { if (p1[0]?.active) triggerReveal(); });

    this.activeModal = { destroy: () => p1.forEach(o => o?.destroy?.()) };
    this.setGameplayEnabled(false);
  }

  _rewardPhase3(reward, stars, revealTheme, primaryInt, primary, W, H, D) {
    const objs = [];
    const push = (o) => { objs.push(o); return o; };

    push(this.add.rectangle(W/2, H/2, W, H, 0x000000, 0.92).setDepth(D));

    // Glow aura behind creature placeholder
    push(this.add.circle(W/2, H*0.38, 80, primaryInt, 0.12).setDepth(D + 1));

    // Creature placeholder — coloured circle (replace with sprite when assets arrive)
    const creatureCircle = push(this.add.circle(W/2, H*0.38, 62, primaryInt, 0.18)
      .setStrokeStyle(3, primaryInt, 0.90)
      .setDepth(D + 2).setScale(0));

    // Creature sprite (ou fallback emoji se sprite não estiver carregado)
    const _spriteKey = `creature-${reward.creatureId}`;
    let creatureIcon;
    if (this.textures.exists(_spriteKey)) {
      creatureIcon = push(this.add.image(W/2, H*0.38, _spriteKey)
        .setDisplaySize(112, 112).setDepth(D + 3).setScale(0));
    } else {
      const icons = { fire: "🔥", water: "💧", earth: "🌿", electric: "⚡", psychic: "🔮" };
      creatureIcon = push(this.add.text(W/2, H*0.38, icons[reward.type] || "✦", { fontSize: "46px" })
        .setOrigin(0.5).setDepth(D + 3).setScale(0));
    }

    // 🔊 SFX HOOK: 'sfx_creature_reveal' — the triumphant "new creature" pop
    // this.sound?.play?.('sfx_creature_reveal', { volume: 1.0 });

    // Pop-in with Back.Out
    this.tweens.add({ targets: [creatureCircle, creatureIcon], scaleX: 1, scaleY: 1, duration: 420, ease: "Back.Out" });

    // Lookup criatura no bestiário (fonte única de verdade — inglês)
    const _creature = BESTIARY.find(c => c.id === reward.creatureId);

    // Typewriter name — usa o nome canónico do bestiário
    const fullName  = (_creature?.name || reward.name || "???").toUpperCase();
    const nameTxt   = push(this.add.text(W/2, H*0.57, "", {
      fontFamily: "system-ui, Arial", fontSize: "22px",
      color: primary, fontStyle: "bold",
    }).setOrigin(0.5).setDepth(D + 3));

    let idx = 0;
    this.time.addEvent({
      delay: 55, repeat: fullName.length - 1,
      callback: () => { idx++; nameTxt.setText(fullName.slice(0, idx)); },
    });

    const afterName = fullName.length * 55 + 150;

    // Flavour text — bestiaryData.text (EN), fallback para text_creature do JSON
    const flavourTxt = push(this.add.text(W/2, H*0.655, _creature?.text || reward.text_creature || "", {
      fontFamily: "system-ui, Arial", fontSize: "12px",
      color: "#94a3b8", align: "center",
      wordWrap: { width: W * 0.80 }, lineSpacing: 4,
    }).setOrigin(0.5).setAlpha(0).setDepth(D + 3));
    this.time.delayedCall(afterName, () => { this.tweens.add({ targets: flavourTxt, alpha: 1, duration: 500 }); });

    // Bestiary badge
    const badge = push(this.add.text(W/2, H*0.775, I18n.t('added_bestiary'), {
      fontFamily: "system-ui, Arial", fontSize: "11px",
      color: revealTheme.secondary, fontStyle: "bold",
    }).setOrigin(0.5).setAlpha(0).setDepth(D + 3));
    this.time.delayedCall(afterName + 500, () => {
      this.tweens.add({ targets: badge, alpha: 1, duration: 400 });
      this.tweens.add({ targets: badge, scaleX: 1.06, scaleY: 1.06, duration: 700, yoyo: true, repeat: -1, ease: "Sine.InOut" });
    });

    // Continue button
    const contBtn = push(this.add.text(W/2, H*0.875, I18n.t('continue_btn'), {
      fontFamily: "system-ui, Arial", fontSize: "15px",
      color: "#ffffff", fontStyle: "bold",
      backgroundColor: primary, padding: { x: 26, y: 11 },
    }).setOrigin(0.5).setAlpha(0).setDepth(D + 3).setInteractive({ useHandCursor: true }));
    this.time.delayedCall(afterName + 950, () => { this.tweens.add({ targets: contBtn, alpha: 1, duration: 400 }); });

    contBtn.on("pointerdown", () => {
      this._destroyModal(objs);
      this.showWinModal(stars);
    });

    this.activeModal = { destroy: () => this._destroyModal(objs) };
  }

  _destroyModal(objs) {
    objs.forEach(o => { try { o?.destroy?.(); } catch (_) {} });
  }

  closeModal(force = false) {
    if (!this.activeModal) return;
    this.activeModal.destroy();
    this.activeModal = null;

    if (!force) {
      this.setGameplayEnabled(true);
    } else {
      this.setGameplayEnabled(false);
    }
  }

  createModalContainer({ title, bodyLines, buttons, titleColor = "#e2e8f0", accentColor = null }) {
    const w = this.scale.width;
    const h = this.scale.height;
    const D = 1000;
    const accentInt = accentColor ? hexToPhaser(accentColor) : 0x334155;

    // ── Dark overlay ─────────────────────────────────────────────────────────
    const overlay = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.72).setDepth(D);

    const panelW = Math.min(460, w - 40);
    const panelH = Math.min(520, h - 180);
    const px     = Math.round(w / 2 - panelW / 2);
    const py     = Math.round(h / 2 - panelH / 2);

    // ── Gradient panel: dark slate top → region-tinted dark bottom ───────────
    let botR = 0x0c, botG = 0x10, botB = 0x1c;
    if (accentColor) {
      const ac = hexToPhaser(accentColor);
      botR = Math.min(255, 0x0a + Math.floor(((ac >> 16) & 0xff) * 0.11));
      botG = Math.min(255, 0x0c + Math.floor(((ac >> 8)  & 0xff) * 0.11));
      botB = Math.min(255, 0x14 + Math.floor(( ac        & 0xff) * 0.11));
    }
    const topColor = 0x080e18;
    const botColor = (botR << 16) | (botG << 8) | botB;

    const panelGfx = this.add.graphics().setDepth(D + 1);
    panelGfx.fillGradientStyle(topColor, topColor, botColor, botColor, 1, 1, 1, 1);
    panelGfx.fillRect(px, py, panelW, panelH);

    // ── Double-line border + corner brackets ──────────────────────────────────
    const borderGfx = this.add.graphics().setDepth(D + 2);

    // Outer glow — wide, low alpha
    borderGfx.lineStyle(5, accentInt, 0.18);
    borderGfx.strokeRect(px - 1, py - 1, panelW + 2, panelH + 2);

    // Inner crisp line
    borderGfx.lineStyle(1, accentInt, 0.80);
    borderGfx.strokeRect(px + 2, py + 2, panelW - 4, panelH - 4);

    // Corner brackets (L-shapes, 18 px arms each)
    const bLen = 18;
    borderGfx.lineStyle(2, accentInt, 1);
    // ┌ top-left
    borderGfx.lineBetween(px,             py + bLen,      px,             py);
    borderGfx.lineBetween(px,             py,             px + bLen,      py);
    // ┐ top-right
    borderGfx.lineBetween(px + panelW - bLen, py,         px + panelW,    py);
    borderGfx.lineBetween(px + panelW,    py,             px + panelW,    py + bLen);
    // └ bottom-left
    borderGfx.lineBetween(px,             py + panelH - bLen, px,         py + panelH);
    borderGfx.lineBetween(px,             py + panelH,    px + bLen,      py + panelH);
    // ┘ bottom-right
    borderGfx.lineBetween(px + panelW - bLen, py + panelH, px + panelW,  py + panelH);
    borderGfx.lineBetween(px + panelW,    py + panelH - bLen, px + panelW, py + panelH);

    // ── Title ─────────────────────────────────────────────────────────────────
    const titleText = this.add
      .text(w / 2, py + 14, title, {
        fontFamily: "system-ui, Arial",
        fontSize: "20px",
        color: titleColor,
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0)
      .setDepth(D + 3);

    // Thin separator below title
    const separatorY = py + 46;
    const sepGfx = this.add.graphics().setDepth(D + 3);
    sepGfx.lineStyle(1, accentInt, 0.40);
    sepGfx.lineBetween(px + 18, separatorY, px + panelW - 18, separatorY);

    // ── Body with text hierarchy ──────────────────────────────────────────────
    // Flatten all lines (formatGoalsText uses \n internally)
    const allLines = bodyLines.flatMap(l => l.split("\n"));
    const bodyObjs = [];
    let curY = separatorY + 10;

    for (const raw of allLines) {
      if (raw === "") { curY += 8; continue; }

      const isBullet = raw.startsWith("•") || raw.startsWith("- ");
      const isEvo    = raw.startsWith("✦");
      // Section header: short word ending with ":" (e.g. "Objetivos:")
      const isHeader = !isBullet && !isEvo && raw.endsWith(":") && raw.length < 22;

      let color, fSize, fStyle;
      if (isBullet)      { color = "#94a3b8"; fSize = "13px"; fStyle = "normal"; }
      else if (isEvo)    { color = titleColor; fSize = "12px"; fStyle = "bold";   }
      else if (isHeader) { color = "#475569";  fSize = "12px"; fStyle = "normal"; }
      else               { color = "#cbd5e1";  fSize = "13px"; fStyle = "normal"; }

      const t = this.add
        .text(px + 18, curY, raw, {
          fontFamily: "system-ui, Arial",
          fontSize: fSize,
          color,
          fontStyle: fStyle,
          wordWrap: { width: panelW - 36 },
          lineSpacing: 3,
        })
        .setOrigin(0, 0)
        .setDepth(D + 3);

      bodyObjs.push(t);
      curY += Math.max(18, t.height) + 4;
    }

    // ── Themed pill buttons ───────────────────────────────────────────────────
    const btnY = py + panelH - 52;
    const gap  = 12;
    const btnW = Math.floor((panelW - 36 - gap * (buttons.length - 1)) / buttons.length);
    const btnH = 40;
    const btnObjs = [];

    for (let i = 0; i < buttons.length; i++) {
      const b  = buttons[i];
      const bx = px + 18 + i * (btnW + gap) + btnW / 2;

      if (b.primary) {
        // Filled pill — region accent fill + white bold text
        const rect = this.add
          .rectangle(bx, btnY, btnW, btnH, accentInt, 0.88)
          .setStrokeStyle(2, accentInt, 1)
          .setDepth(D + 3)
          .setInteractive({ useHandCursor: true });

        const txt = this.add
          .text(bx, btnY, b.label, {
            fontFamily: "system-ui, Arial",
            fontSize: "14px",
            color: "#ffffff",
            fontStyle: "bold",
          })
          .setOrigin(0.5)
          .setDepth(D + 4);

        rect.on("pointerover",  () => rect.setAlpha(1));
        rect.on("pointerout",   () => rect.setAlpha(0.88));
        rect.on("pointerdown",  () => {
          this.tweens.add({ targets: rect, scale: 0.97, yoyo: true, duration: 70 });
          b.onClick?.();
        });
        btnObjs.push(rect, txt);

      } else {
        // Outlined pill — transparent fill + muted border
        const rect = this.add
          .rectangle(bx, btnY, btnW, btnH, 0x000000, 0.01)
          .setStrokeStyle(1, 0x475569, 1)
          .setDepth(D + 3)
          .setInteractive({ useHandCursor: true });

        const txt = this.add
          .text(bx, btnY, b.label, {
            fontFamily: "system-ui, Arial",
            fontSize: "14px",
            color: "#94a3b8",
          })
          .setOrigin(0.5)
          .setDepth(D + 4);

        rect.on("pointerover",  () => { rect.setStrokeStyle(1, accentInt, 0.70); txt.setColor("#e2e8f0"); });
        rect.on("pointerout",   () => { rect.setStrokeStyle(1, 0x475569,  1);    txt.setColor("#94a3b8"); });
        rect.on("pointerdown",  () => {
          this.tweens.add({ targets: rect, scale: 0.97, yoyo: true, duration: 70 });
          b.onClick?.();
        });
        btnObjs.push(rect, txt);
      }
    }

    // Container at origin so world coords == local coords for all children
    const container = this.add
      .container(0, 0, [overlay, panelGfx, borderGfx, sepGfx, titleText, ...bodyObjs, ...btnObjs])
      .setDepth(D);

    return container;
  }

  formatGoalsText(goals) {
    if (!goals || goals.length === 0) return I18n.t('no_goals');

    const lines = [I18n.t('goals_header')];
    for (const g of goals) {
      if (g.type === "collect") lines.push(I18n.t('goal_collect', { n: g.count, color: g.color.toUpperCase() }));
      else if (g.type === "score") lines.push(I18n.t('goal_score', { n: g.target }));
      else if (g.type === "collect_fragments") lines.push(I18n.t('goal_fragments', { n: g.count }));
      else lines.push(`• ${g.type}`);
    }
    return lines.join("\n");
  }

  computeStars() {
    let target = null;
    const scoreGoal = this.levelGoals.find((g) => g.type === "score");
    if (scoreGoal) target = scoreGoal.target;
    if (!target) target = Math.max(800, (this.levelDef?.moves || 18) * 150);

    let stars = 1;
    if (this.score >= Math.floor(target * 1.25)) stars = 2;
    if (this.score >= Math.floor(target * 1.6)) stars = 3;
    return stars;
  }

  // -----------------------------
  // WORLD / TILE
  // -----------------------------
  worldToTile(x, y) {
    const left = this.boardX;
    const top = this.boardY;
    const right = left + this.cols * this.tileSize;
    const bottom = top + this.rows * this.tileSize;

    if (x < left || x >= right || y < top || y >= bottom) return null;

    const c = Math.floor((x - left) / this.tileSize);
    const r = Math.floor((y - top) / this.tileSize);
    if (r < 0 || r >= this.rows || c < 0 || c >= this.cols) return null;

    return { r, c };
  }

  // -----------------------------------------
  // INPUT / SWAP
  // -----------------------------------------
  async handleTileTap(r, c) {
    if (this.isBusy) return;

    // skill pending -> clique é alvo
    if (this.pendingSkill) {
      await this.armSkillOnTile(r, c);
      return;
    }

    // seleção normal
    if (!this.selected) {
      this.selected = { r, c };
      this.board.highlight(r, c, true);
      // bounce feedback on selection
      const sprite = this.board.grid[r]?.[c]?.sprite;
      if (sprite) {
        this.tweens.add({ targets: sprite, scale: 1.12, duration: 70, yoyo: true, ease: 'Back.Out' });
      }
      return;
    }

    const a = this.selected;
    const b = { r, c };

    if (a.r === b.r && a.c === b.c) {
      this.board.highlight(a.r, a.c, false);
      this.selected = null;
      return;
    }

    const isAdjacent = Math.abs(a.r - b.r) + Math.abs(a.c - b.c) === 1;

    this.board.highlight(a.r, a.c, false);
    this.selected = null;

    if (!isAdjacent) return;

    await this._executeSwap(a, b);
  }

  async handleTileSwap(r, c, dr, dc) {
    if (this.isBusy) return;
    if (this.pendingSkill) {
      await this.armSkillOnTile(r, c);
      return;
    }
    const rows = this.board.grid.length;
    const cols = this.board.grid[0]?.length || 0;
    const nr = r + dr;
    const nc = c + dc;
    if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) return;
    await this._executeSwap({ r, c }, { r: nr, c: nc });
  }

  async _executeSwap(a, b) {
    this.isBusy = true;
    this._recorder.recordSwap(a, b);
    try {
      await this.board.swapAnimated(a, b);

      const { groups, all } = findMatchGroups(this.board.grid);
      if (all.length === 0) {
        await this.board.swapAnimated(a, b);
        this._recorder.discardTurn();
        this.moodEngine?.onFailedMove();
        const failTrigger = this.moodEngine?._failStreak >= 2 ? 'fail_streak' : 'fail_small';
        this.moodEngine?.tryMoodEvent(failTrigger, { ...this._moodState(), isBusy: false });
        return;
      }

      // Background burst on match
      this._bgBurst?.({ type: 'match', comboLevel: 1 });

      // skill armada valida pelo MAIOR grupo desta jogada
      await this.maybeResolveArmedSkillFromGroups(groups);

      if (!this.infinite) {
        this.movesLeft -= 1;
        this.hud.setMoves(this.movesLeft);
      }

      // resolve cascatas + score/combos + XP
      await this.resolveMatchesLoop();
      this._recorder.finishTurn({ score: this.score, movesLeft: this.movesLeft });

      // Background cascade-aware burst (fires after resolveMatchesLoop sets _lastCascadeIndex)
      if (this._lastCascadeIndex >= 2) {
        const _t = this._lastCascadeIndex >= 3 ? 'big_combo' : 'combo';
        this._bgBurst?.({ type: _t, comboLevel: this._lastCascadeIndex });
      }

      if (this.infinite) {
        // Infinite: wave progression, no win/fail via moves
        this._checkInfiniteWave();
        return;
      }

      this.moodEngine?.onPlayerAction();

      // Mood: combo feedback based on max cascade this turn
      const _moodStateNow = { ...this._moodState(), isBusy: false };
      if (this._lastCascadeIndex >= 3) {
        this.moodEngine?.tryMoodEvent('combo_big', _moodStateNow);
      } else if (this._lastCascadeIndex === 2) {
        this.moodEngine?.tryMoodEvent('combo_small', _moodStateNow);
      }

      // Mood: near_win when goal progress crosses 70%
      if (this._getGoalProgress() >= 0.7) {
        this.moodEngine?.tryMoodEvent('near_win', _moodStateNow);
      }

      // vitória?
      if (this.checkAllGoalsComplete()) {
        this.onWin();
        return;
      }

      // derrota?
      if (this.movesLeft <= 0) {
        this.onFail();
        return;
      }
    } catch (e) {
      console.error("Erro na jogada:", e);
      this.hud.setStatus(I18n.t('error_msg'));
    } finally {
      this.isBusy = false;
    }
  }

  checkAllGoalsComplete() {
    if (!this.levelGoals || this.levelGoals.length === 0) return false;

    for (const g of this.levelGoals) {
      if (g.type === "collect") {
        if ((this.collected[g.color] || 0) < g.count) return false;
      } else if (g.type === "score") {
        if (this.score < g.target) return false;
      } else if (g.type === "collect_fragments") {
        if (this.fragmentsEarnedThisLevel < g.count) return false;
      }
    }
    return true;
  }

  // 🔊 Fade suave da música (vitória/derrota)
  _fadeOutBgm(targetVol = 0, duration = 1000) {
    if (!this.bgm?.isPlaying) return;
    this.tweens.add({ targets: this.bgm, volume: targetVol, duration });
  }

  // ── Timer helpers ─────────────────────────────────────────────────────────
  _startTimer() {
    this._timerEvent = this.time.addEvent({
      delay: 1000,
      repeat: this.timeLeft - 1,
      callback: () => {
        this.timeLeft = Math.max(0, this.timeLeft - 1);
        this.hud.setTimer(this.timeLeft);
        // Timer chegou a 0
        if (this.timeLeft === 0) {
          if (this.infinite) {
            this.onInfiniteGameOver();
          } else {
            this.onLevelLose(); // tempo esgotado = derrota
          }
        }
      },
    });
  }

  _stopTimer() {
    if (this._timerEvent) {
      this._timerEvent.remove(false);
      this._timerEvent = null;
    }
    this.timeBonusEarned = this.timeLeft > 0 ? this.timeLeft * 10 : 0;
    this.hud.hideTimer();
  }

  // 🔊 SFX volume helper — scales base volume by user setting
  _sfxVol(base) { return base * Settings.sfxVolume; }

  // 🔊 Som de match — varia entre normal (x1) e combo (x2+)
  _playMatchSfx(cascadeIndex = 1) {
    const pool = cascadeIndex >= 2
      ? ['sfx_match3', 'sfx_match4']
      : ['sfx_match1', 'sfx_match2'];
    const key = pool[Math.floor(Math.random() * pool.length)];
    try { this.sound.play(key, { volume: this._sfxVol(cascadeIndex >= 2 ? 0.5 : 0.4) }); } catch (_) {}
  }

  // 🔊 Som do skill: apenas elemento (vozes japonesas desativadas)
  _playSkillSfx(monsterId, skillLevel = 1) {
    const elemMap = {
      fogo:     'sfx_fire_power',
      raio:     'sfx_electric_power',
      jato:     'sfx_water_power',
      terra:    'sfx_earth_power',
      controlo: 'sfx_psychic_power',
    };
    // const voiceMap = { 1: 'sfx_attack', 2: 'sfx_super_attack', 3: 'sfx_megaattack' }; // vozes — desativadas
    try { this.sound.play(elemMap[monsterId] || 'sfx_fire_power', { volume: this._sfxVol(0.7) }); } catch (_) {}
    // this.time.delayedCall(200, () => {
    //   try { this.sound.play(voiceMap[skillLevel] || 'sfx_attack', { volume: 0.65 }); } catch (_) {}
    // });
  }

  onWin() {
    const _isClutch = this.movesLeft <= 2 || this.timeLeft <= 5;
    this.moodEngine?.tryMoodEvent(_isClutch ? 'victory_clutch' : 'victory_clean', { ...this._moodState(), isBusy: false });
    this._stopTimer(); // para timer e calcula timeBonusEarned
    this.score += this.timeBonusEarned; // bonus sem penalidade

    // 🎉 Happy jump — todos os monstros em cascata
    this.monsterBar.monsters.forEach((m, i) => {
      const icon = this.monsterBar.items[i]?.icon;
      if (!icon?.active) return;
      this.tweens.killTweensOf(icon);
      const baseY = icon._baseY ?? icon.y;
      const bs = icon._baseSX ?? icon.scaleX; // escala base relativa ao setDisplaySize
      this.time.delayedCall(i * 160, () => {
        this.tweens.add({
          targets: icon, y: baseY - 30, scaleX: bs * 1.15, scaleY: bs * 1.15,
          duration: 420, ease: 'Back.Out', yoyo: true, repeat: 1, repeatDelay: 100,
        });
      });
    });

    this._recorder.recordVictory({
      score: this.score,
      stars: this.computeStars(),
      maxCombo: this.maxComboMultiplier,
      timeBonus: this.timeBonusEarned,
    });

    this._fadeOutBgm(0.25, 1200); // 🔊 baixa para 25% em 1.2s
    try { this.sound.play('sfx_victory', { volume: this._sfxVol(0.85) }); } catch (_) {} // 🔊
    const stars = this.computeStars();

    // Check first-win BEFORE saving (reward only triggers once)
    const wasAlreadyWon = LevelStore.getStars(this.levelId, this.regionId) > 0;

    LevelStore.recordWin({ levelId: this.levelId, regionId: this.regionId, stars, score: this.score });

    try {
      const { leveledUp } = LevelStore.applyMonsterXp(this.xpGains);
      this.justLeveledUp = leveledUp || [];
    } catch (e) {
      console.warn("applyMonsterXp falhou:", e);
      this.justLeveledUp = [];
    }

    // ── LOOTBOX REWARD (sempre ao vencer) ────────────────────────────────
    // Boss levels (10, 20, 30) ganham lootbox melhorada
    const isBoss   = [10, 20, 30].includes(this.levelId);
    const drops    = isBoss ? generateBossLootbox(this.regionId) : generateLootbox(this.regionId);

    const nextId  = this.levelId + 1;
    const hasNext = !!LevelStore.getLevel(nextId, this.regionId);

    this._playVictoryCelebration(stars, drops, nextId, hasNext, isBoss);

    // ── BESTIÁRIO (apêndice — mantido para Story Mode) ────────────────────
    // const reward = this.levelDef?.reward;
    // const _cId = reward?.creatureId || reward?.name;
    // if (reward && !wasAlreadyWon && !LevelStore.hasCreature(_cId)) {
    //   LevelStore.unlockCreature(_cId);
    //   this.showRewardRevealModal(reward, stars);
    //   return;
    // }
    // this.showWinModal(stars);
  }

  // ── Victory celebration — particle burst + score highlight + stars + auto-advance ──
  _playVictoryCelebration(stars, drops, nextId, hasNext, isBoss) {
    const theme     = RegionThemes[this.regionId] || RegionThemes.volcano;
    const primary   = hexToPhaser(theme.primary);
    const secondary = hexToPhaser(theme.secondary);
    const W  = this.scale.width;
    const H  = this.scale.height;
    const D  = 1100;
    const bCX = this.boardX + (this.cols * this.tileSize) / 2;
    const bCY = this.boardY + (this.rows * this.tileSize) / 2;

    // ── 1. Full-screen flash ──────────────────────────────────────────────
    const flash = this.add.rectangle(W / 2, H / 2, W, H, 0xffffff, 0).setDepth(D + 10);
    this.tweens.add({
      targets: flash, alpha: { from: 0.4, to: 0 },
      duration: 450, ease: 'Quad.Out',
      onComplete: () => flash.destroy(),
    });

    // ── 2. Burst ring from board centre ───────────────────────────────────
    const BURST = 80;
    for (let i = 0; i < BURST; i++) {
      const angle = (i / BURST) * Math.PI * 2;
      const dist  = Phaser.Math.Between(260, 520);
      const size  = Phaser.Math.FloatBetween(6, 16);
      const color = i % 3 === 0 ? secondary : primary;
      const p = this.add.rectangle(bCX, bCY, size, size, color, 1).setDepth(D);
      this.tweens.add({
        targets: p,
        x: bCX + Math.cos(angle) * dist,
        y: bCY + Math.sin(angle) * dist,
        alpha: 0, scaleX: 0.2, scaleY: 0.2,
        duration: Phaser.Math.Between(800, 1400),
        ease: 'Quad.Out',
        delay: Phaser.Math.Between(0, 120),
        onComplete: () => p.destroy(),
      });
    }

    // Second white ring
    this.time.delayedCall(100, () => {
      for (let i = 0; i < 55; i++) {
        const angle = (i / 55) * Math.PI * 2 + 0.15;
        const dist  = Phaser.Math.Between(150, 380);
        const sz    = Phaser.Math.FloatBetween(5, 12);
        const p = this.add.rectangle(bCX, bCY, sz, sz, 0xffffff, 1).setDepth(D);
        this.tweens.add({
          targets: p,
          x: bCX + Math.cos(angle) * dist,
          y: bCY + Math.sin(angle) * dist,
          alpha: 0,
          duration: Phaser.Math.Between(600, 1100),
          ease: 'Quad.Out',
          onComplete: () => p.destroy(),
        });
      }
    });

    // Third gold ring
    this.time.delayedCall(220, () => {
      for (let i = 0; i < 50; i++) {
        const angle = (i / 50) * Math.PI * 2;
        const dist  = Phaser.Math.Between(200, 460);
        const sz    = Phaser.Math.FloatBetween(5, 13);
        const p = this.add.rectangle(bCX, bCY, sz, sz, 0xfacc15, 1).setDepth(D);
        this.tweens.add({
          targets: p,
          x: bCX + Math.cos(angle) * dist,
          y: bCY + Math.sin(angle) * dist,
          alpha: 0,
          duration: Phaser.Math.Between(700, 1200),
          ease: 'Quad.Out',
          onComplete: () => p.destroy(),
        });
      }
    });

    // Fourth region-colour ring — extra wave
    this.time.delayedCall(380, () => {
      for (let i = 0; i < 40; i++) {
        const angle = (i / 40) * Math.PI * 2 + 0.3;
        const dist  = Phaser.Math.Between(180, 500);
        const sz    = Phaser.Math.FloatBetween(4, 11);
        const color = i % 2 === 0 ? primary : secondary;
        const p = this.add.rectangle(bCX, bCY, sz, sz, color, 1).setDepth(D);
        this.tweens.add({
          targets: p,
          x: bCX + Math.cos(angle) * dist,
          y: bCY + Math.sin(angle) * dist,
          alpha: 0,
          duration: Phaser.Math.Between(600, 1100),
          ease: 'Quad.Out',
          onComplete: () => p.destroy(),
        });
      }
    });

    // ── 3. Confetti rain ──────────────────────────────────────────────────
    const confetti = [primary, secondary, 0xffffff, 0xfacc15];
    for (let i = 0; i < 120; i++) {
      this.time.delayedCall(i * 12, () => {
        const x = Phaser.Math.Between(10, W - 10);
        const sz = Phaser.Math.FloatBetween(4, 11);
        const c  = confetti[Math.floor(Math.random() * confetti.length)];
        const p  = this.add.rectangle(x, -8, sz, sz, c, 1).setDepth(D);
        this.tweens.add({
          targets: p,
          y: H + 20,
          x: x + Phaser.Math.Between(-90, 90),
          alpha: 0,
          duration: Phaser.Math.Between(1000, 2000),
          ease: 'Quad.In',
          onComplete: () => p.destroy(),
        });
      });
    }

    // ── 4. Stars pop in one by one ────────────────────────────────────────
    const starY = H * 0.34;
    const gap   = 80;
    const sx0   = W / 2 - gap;
    for (let i = 0; i < 3; i++) {
      const filled = i < stars;
      this.time.delayedCall(280 + i * 210, () => {
        const star = this.add.text(sx0 + i * gap, starY, filled ? '★' : '☆', {
          fontSize: '72px', fontFamily: 'Kamikaze, Arial',
          color: filled ? '#facc15' : '#4b5563',
          stroke: filled ? '#92400e' : '#111827',
          strokeThickness: 5,
        }).setOrigin(0.5).setScale(0).setDepth(D + 5).setAlpha(1);

        this.tweens.add({ targets: star, scaleX: 1, scaleY: 1, duration: 270, ease: 'Back.Out(2.5)' });

        if (filled) {
          for (let j = 0; j < 12; j++) {
            const a = (j / 12) * Math.PI * 2;
            const sp = this.add.rectangle(sx0 + i * gap, starY, 4, 4, 0xfacc15, 1).setDepth(D + 4);
            this.tweens.add({
              targets: sp,
              x: (sx0 + i * gap) + Math.cos(a) * 55,
              y: starY + Math.sin(a) * 55,
              alpha: 0, duration: 380, ease: 'Quad.Out',
              onComplete: () => sp.destroy(),
            });
          }
        }
      });
    }

    // ── 5. Score + objective area highlight ───────────────────────────────
    this.time.delayedCall(180, () => {
      // Score area glow
      const sg = this.add.rectangle(W / 2, 72, W - 30, 58, primary, 0).setDepth(D - 1);
      this.tweens.add({
        targets: sg, alpha: { from: 0.35, to: 0 },
        duration: 700, ease: 'Quad.Out',
        onComplete: () => sg.destroy(),
      });
      // Goal bar pulse
      const gb = this.add.rectangle(W / 2, 105, W - 28, 14, primary, 0).setDepth(D - 1);
      this.tweens.add({
        targets: gb, alpha: { from: 0.5, to: 0 },
        duration: 800, ease: 'Quad.Out',
        onComplete: () => gb.destroy(),
      });
    });

    // ── 6. "Create Clip" button (Pazurumon Camera) ────────────────────────
    const _lootboxData = { drops, stars, regionId: this.regionId, nextLevelId: hasNext ? nextId : null, isBoss };
    const _goToLootbox = () => {
      this.cameras.main.fadeOut(175, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('LootboxScene', _lootboxData);
      });
    };

    if (this._recorder.hasReplay()) {
      this.time.delayedCall(1200, () => {
        const clipBtn = this.add.text(W / 2, H * 0.72, I18n.t('replay_btn'), {
          fontFamily: 'Kamikaze, system-ui, Arial',
          fontSize: '18px',
          color: '#0d1525',
          backgroundColor: '#fbbf24',
          padding: { x: 18, y: 10 },
        }).setOrigin(0.5).setDepth(D + 20).setScale(0)
          .setInteractive({ useHandCursor: true });

        this.tweens.add({
          targets: clipBtn, scaleX: 1, scaleY: 1,
          duration: 280, ease: 'Back.Out(2)',
        });

        clipBtn.on('pointerup', () => {
          clipBtn.destroy();
          this.cameras.main.fadeOut(175, 0, 0, 0);
          this.cameras.main.once('camerafadeoutcomplete', () => {
            this.scene.start('ReplayScene', {
              replayData: this._recorder.getReplayData(),
              returnTo: 'LootboxScene',
              returnData: _lootboxData,
            });
          });
        });

        // Auto-advance after 7s if clip button not tapped
        this.time.delayedCall(7000, () => {
          if (clipBtn.active) {
            clipBtn.destroy();
            _goToLootbox();
          }
        });
      });
    } else {
      // No replay data — auto-advance at 1.8s (original flow)
      this.time.delayedCall(1800, _goToLootbox);
    }
  }

  onFail() {
    this._recorder.recordDefeat({ score: this.score });
    this._stopTimer(); // para timer (sem bonus em derrota)

    // 😱 Terrified — todos os monstros tremem com tint azul
    this.monsterBar.monsters.forEach((m, i) => {
      const icon = this.monsterBar.items[i]?.icon;
      if (!icon?.active) return;
      this.tweens.killTweensOf(icon);
      const baseX = icon.x;
      const bsx = icon._baseSX ?? icon.scaleX; // escala base relativa ao setDisplaySize
      const bsy = icon._baseSY ?? icon.scaleY;
      icon.setTint(0x8888ff);
      icon.setScale(bsx * 0.88, bsy * 0.88); // escala relativa, não absoluta
      this.tweens.add({
        targets: icon, x: baseX - 5, duration: 60, ease: 'Linear',
        yoyo: true, repeat: 8,
        onComplete: () => { if (icon.active) { icon.setX(baseX); icon.setScale(bsx, bsy); } },
      });
    });

    this._fadeOutBgm(0.1, 1600);
    try { this.sound.play('sfx_looselevel', { volume: this._sfxVol(0.8) }); } catch (_) {}
    this._playDefeatFlow();
  }

  // ── Defeat flow — dark overlay + camera drop + delayed modal ────────────
  _playDefeatFlow() {
    const W = this.scale.width;
    const H = this.scale.height;
    const D = 1050;

    // Short sharp shake — the world collapses
    this.cameras.main.shake(180, 0.018);

    // Dark vignette creeps in over the board
    const darkness = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0).setDepth(D);
    this.tweens.add({
      targets: darkness,
      alpha: 0.72,
      duration: 1100,
      ease: 'Quad.In',
    });

    // Desaturate — red tint wash fades in slowly
    const redWash = this.add.rectangle(W / 2, H / 2, W, H, 0x330000, 0).setDepth(D + 1);
    this.tweens.add({
      targets: redWash,
      alpha: 0.18,
      duration: 900,
      ease: 'Quad.In',
    });

    // Board area dims harder than the rest
    const boardW = this.cols * this.tileSize;
    const boardH = this.rows * this.tileSize;
    const boardDim = this.add.rectangle(
      this.boardX + boardW / 2, this.boardY + boardH / 2,
      boardW, boardH, 0x000000, 0
    ).setDepth(D + 2);
    this.tweens.add({
      targets: boardDim,
      alpha: 0.55,
      duration: 800,
      ease: 'Quad.In',
    });

    // Show defeat modal after the darkness settles
    this.time.delayedCall(1300, () => {
      // Clean up overlays before modal takes over
      this.tweens.killTweensOf(darkness);
      this.tweens.killTweensOf(redWash);
      this.tweens.killTweensOf(boardDim);
      darkness.destroy();
      redWash.destroy();
      boardDim.destroy();
      this.showDefeatModal();
    });
  }

  async resolveMatchesLoop() {
    let cascadeIndex = 0;

    while (true) {
      const { groups, all } = findMatchGroups(this.board.grid);
      if (all.length === 0) break;

      cascadeIndex += 1;
      this._lastCascadeIndex = cascadeIndex;
      this._playMatchSfx(cascadeIndex); // 🔊 match sound (normal vs combo)
      const comboMultiplier = Math.min(Theme.vfx.comboCap ?? 10, cascadeIndex); // x1..x10
      this.maxComboMultiplier = Math.max(this.maxComboMultiplier, comboMultiplier);

      // energia por cor -> MonsterBar
      const gainByColor = { red: 0, yellow: 0, green: 0, blue: 0, purple: 0 };
      for (const g of groups) {
        const p = g[0];
        const t = this.board.grid[p.r][p.c];
        if (!t) continue;
        gainByColor[t.color] += energyForGroupSize(g.length);
      }
      this.monsterBar.addEnergyByColor(gainByColor);

      // objetivos collect: contar
      for (const pos of all) {
        const t = this.board.grid[pos.r][pos.c];
        if (t) this.collected[t.color] += 1;
      }
      if (this.goal?.type === "collect") {
        this.hud.setGoal(this.goal, this.collected[this.goal.color] || 0,
          this.goal2, this.goal2 ? (this.collected[this.goal2.color] || 0) : undefined);
      }

      // ✅ XP: contar tiles removidos por cor
      const clearedByColor = { red: 0, yellow: 0, green: 0, blue: 0, purple: 0 };
      for (const pos of all) {
        const t = this.board.grid[pos.r]?.[pos.c];
        if (!t) continue;
        clearedByColor[t.color] = (clearedByColor[t.color] || 0) + 1;
      }
      this.addXpForClearedTiles(clearedByColor, comboMultiplier);
      this._refreshMonsterXpBars();

      // score base
      const baseScore = all.length * 10 * comboMultiplier;

      // bonus por maior grupo
      let bestSize = 0;
      for (const g of groups) bestSize = Math.max(bestSize, g.length);

      let bonus = 0;
      if (bestSize === 4) bonus += 50;
      else if (bestSize === 5) bonus += 120;
      else if (bestSize >= 6) bonus += 250;

      // ── Fragmentos de Prismara ── combos 4+ geram fragmentos ─────────────
      if (bestSize >= 4) {
        const colorToElement = { red:"fire", blue:"water", green:"earth", yellow:"electric", purple:"aether" };
        const firstGroup = groups.reduce((a, b) => b.length > a.length ? b : a, groups[0]);
        const firstTile  = this.board.grid[firstGroup[0].r]?.[firstGroup[0].c];
        const element    = colorToElement[firstTile?.color] || "fire";
        Collection.awardFragments(element, bestSize);
        this.hud.setFragments?.();
        // rastrear fragmentos ganhos neste nível (6 elementos × bónus)
        const fragsAwarded = bestSize >= 6 ? (20 + 6 * 5) : bestSize >= 5 ? (6 * 10) : 5;
        this.fragmentsEarnedThisLevel += fragsAwarded;
        if (this.goal?.type === "collect_fragments") {
          this.hud.setGoal(this.goal, this.fragmentsEarnedThisLevel);
        }
      }

      const waveMult = this.infinite ? this._getWaveMultiplier() : 1;
      const gained = Math.round((baseScore + bonus) * waveMult);
      this.score += gained;
	  this.gainXP(Math.floor(gained / 10));
      this.hud.setScore?.(this.score);
	  if (this.goal?.type === "score") {
	  this.hud.setGoal(this.goal, this.score);
}

    if (comboMultiplier > 1) {
      const comboTheme = RegionThemes[this.regionId] || RegionThemes.volcano;
      spawnComboText(this, this.scale.width / 2, 250, comboMultiplier, comboTheme.primary);
      // showEvent de combo removido — spawnComboText é o canal único
    }

      this.hud.setCombo?.(this.maxComboMultiplier);
      this.spawnScoreFeedback(gained, all, comboMultiplier, bonus, bestSize);
	  
      // FX: partículas por tile removido — cor do tile
      for (const pos of all) {
        const wp       = this.board.worldPos(pos.r, pos.c);
        const tileHex  = tileColorToHex(this.board.grid[pos.r]?.[pos.c]?.color);
        spawnMatchParticles(this, wp.x, wp.y, tileHex);
      }

      // FX: camera shake + hit-stop on big clears
      if (bestSize >= 6) {
        this._hitStop(70);
        this.cameras.main.shake(120, 0.010);
      } else if (bestSize >= 5) {
        this._hitStop(45);
        this.cameras.main.shake(80, 0.005);
      }

      // FX: board frame flashes element colour on cascade ×2+
      if (comboMultiplier >= 2 && this._boardFrame) {
        const ELEM_CLR = { fire: 0xef4444, electric: 0xfacc15, water: 0x38bdf8, earth: 0x4ade80, psychic: 0xc084fc };
        const monster  = this.monsterBar?.getActiveMonster?.();
        const clr      = ELEM_CLR[monster?.element] || 0xffffff;
        const intensity = Math.min(1, 0.35 + (comboMultiplier - 2) * 0.15);
        this.tweens.killTweensOf(this._boardFrame);
        this.tweens.add({
          targets:  this._boardFrame,
          alpha:    { from: intensity, to: 0.55 },
          duration: 350,
          ease:     'Sine.Out',
          onStart:  () => this._boardFrame?.setTint?.(clr),
          onComplete: () => {
            this._boardFrame?.clearTint?.();
            // resume idle pulse
            this.tweens.add({
              targets: this._boardFrame, alpha: { from: 0.55, to: 1.0 },
              duration: 1900, yoyo: true, repeat: -1, ease: 'Sine.InOut',
            });
          },
        });
      }

      await this.board.removeTilesAnimated(all);
      await resolveGravityAndRefill(this.board);

      // ── Replay: record cascade result ─────────────────────────────────
      this._recorder.recordCascade({
        matched: all,
        comboMultiplier,
        scoreGained: gained,
        board: this.board,
      });

      if (!hasPossibleMoves(this.board.grid)) {
        this._setStatus(I18n.t('shuffle_msg'), 600);
        shuffleBoard(this.board);
      }
    }
  }

// ===== STATUS BAR (race-condition-safe) =====

  // Substitui hud.setStatus() + time.delayedCall() avulso.
  // Cancela sempre o timer anterior para que mensagens antigas
  // não apaguem mensagens mais recentes.
  _setStatus(msg, duration = 0) {
    if (this._statusTimer) {
      this._statusTimer.remove();
      this._statusTimer = null;
    }
    this.hud.setStatus(msg);
    if (duration > 0) {
      this._statusTimer = this.time.delayedCall(duration, () => {
        this.hud.setStatus('');
        this._statusTimer = null;
      });
    }
  }

  // ── Mood Engine: current game state snapshot ──────────────────────────────
  _moodState() {
    return {
      isBusy:       this.isBusy,
      activeModal:  !!this.activeModal,
      moodVisible:  this.moodPresenter?.isVisible ?? false,
      goalProgress: this._getGoalProgress(),
    };
  }

  _getGoalProgress() {
    if (!this.goal) return 0;
    if (this.goal.type === 'collect') {
      const g = this.levelGoals.find(g => g.type === 'collect' && g.color === this.goal.color);
      if (!g) return 0;
      return Math.min(1, (this.collected[g.color] || 0) / g.count);
    }
    if (this.goal.type === 'score') {
      return this.goal.target > 0 ? Math.min(1, this.score / this.goal.target) : 0;
    }
    if (this.goal.type === 'collect_fragments') {
      const g = this.levelGoals.find(g => g.type === 'collect_fragments');
      if (!g) return 0;
      return Math.min(1, this.fragmentsEarnedThisLevel / g.count);
    }
    return 0;
  }

// ===== XP SYSTEM =====

  // Atualiza barras de XP do MonsterBar com preview (XP guardado + ganhos desta sessão)
  _refreshMonsterXpBars() {
    if (!this.monsterBar) return;
    const MONSTERS = ["fogo", "raio", "jato", "terra", "controlo"];
    for (const id of MONSTERS) {
      const stored  = LevelStore.getMonsterXp?.(id) || 0;
      const session = this.xpGains?.[id] || 0;
      const level   = LevelStore.getMonsterLevel?.(id) || 1;
      this.monsterBar.setMonsterXP(id, stored + session, level);
    }
  }

gainXP(amount) {
  this.playerXP += amount;

  // level up loop (caso ganhe muito XP de uma vez)
  while (this.playerXP >= this.nextLevelXP) {
    this.playerXP -= this.nextLevelXP;
    this.playerLevel++;
    this.nextLevelXP = Math.floor(this.nextLevelXP * 1.35);

    this._setStatus(I18n.t('level_up_msg', { n: this.playerLevel }), 1500);
  }

  // Persistir nível de Treinador e atualizar HUD
  LevelStore.saveTrainerProgress(this.playerLevel, this.playerXP, this.nextLevelXP);
  this.hud.setLevel?.(this.playerLevel, this.playerXP, this.nextLevelXP);
}

  // -----------------------------------------
  // SKILL FLOW (preview -> armed -> next match validates -> cast/fail)
  // -----------------------------------------
  requestUseSkill(monster) {
    if (this.isBusy) return;
    if (!monster || !monster.ready) return;

    // se já havia uma armada, troca (sem consumir energia)
    if (this.armedSkill) {
      const old = this.armedSkill.monsterId;
      this.armedSkill = null;
      this.onMonsterIdle(old);
    }

    this.previewJustArmed = true;
    this.time.delayedCall(0, () => (this.previewJustArmed = false));

    // hooks para animação
    this.onMonsterReady(monster.id);

    // entra em modo escolher alvo + preview
    this.pendingSkill = { monsterId: monster.id };
    this.previewActive = true;
    this.previewLast = null;

    const req = this.getRequiredColorForMonster(monster.id);
    const name = req ? req.toUpperCase() : "???";
    this.hud.setStatus(I18n.t('skill_target', { color: name }));
    this._showCancelButton(monster.id);

    // Flash matching tiles on the board so player knows which ones to aim for
    if (req) this._flashColorTiles(req);
  }

  showPreview(r, c) {
    this.clearPreview();

    const monsterId = this.pendingSkill?.monsterId;
    if (!monsterId) return;

    // Only show preview on tiles that match the required color
    const requiredColor = this.getRequiredColorForMonster(monsterId);
    const hoveredTile = this.board.grid[r]?.[c];
    if (requiredColor && hoveredTile && hoveredTile.color !== requiredColor) return;

    const affected = getAffectedPositions(this.board.grid, monsterId, r, c);
    if (!affected || affected.length === 0) return;

    for (const pos of affected) {
      const t = this.board.grid[pos.r][pos.c];
      if (!t) continue;

      const rect = this.add
        .rectangle(t.sprite.x, t.sprite.y, this.tileSize - 6, this.tileSize - 6, 0xffffff, 0.22)
        .setDepth(200);

      this.previewTiles.push(rect);
    }
  }

  clearPreview() {
    for (const o of this.previewTiles) o.destroy();
    this.previewTiles.length = 0;
  }

  cancelSkillPreview() {
    this.previewActive = false;
    this.pendingSkill = null;
    this.previewLast = null;
    this.clearPreview();
    this.hud.setStatus("");
    this._hideCancelButton();
    this._stopColorFlash();
  }

  // escolher alvo -> arma a skill
  async armSkillOnTile(r, c) {
    if (this.isBusy) return;
    if (!this.pendingSkill) return;

    const monsterId = this.pendingSkill.monsterId;
    const requiredColor = this.getRequiredColorForMonster(monsterId);
    if (!requiredColor) return;

    // sai do preview
    this.previewActive = false;
    this.pendingSkill = null;
    this.previewLast = null;
    this.clearPreview();

    // controlo always clears its own element colour (purple) — never depends on the tapped tile
    const selectedColor = monsterId === "controlo" ? requiredColor : (this.board.grid[r]?.[c]?.color || null);
    this.armedSkill = { monsterId, requiredColor, target: { r, c }, selectedColor };

    this.hud.setStatus(I18n.t('skill_armed', { color: requiredColor.toUpperCase() }));
  }

  // valida pela cor do MAIOR grupo
  async maybeResolveArmedSkillFromGroups(groups) {
    if (!this.armedSkill) return;
    if (!groups || groups.length === 0) return;

    let best = groups[0];
    for (const g of groups) if (g.length > best.length) best = g;

    const p = best[0];
    const t = this.board.grid[p.r][p.c];
    const largestColor = t?.color || null;

    const { monsterId, requiredColor, target, selectedColor } = this.armedSkill;

    // consome "tentativa"
    this.armedSkill = null;

    if (largestColor === requiredColor) {
      // controlo: find a tile matching selectedColor on the current board
      // fallback chain: selectedColor → requiredColor → skip (no valid tile found)
      let castR = -1, castC = -1;
      if (monsterId === "controlo") {
        const searchColors = selectedColor ? [selectedColor, requiredColor] : [requiredColor];
        outer: for (const col of searchColors) {
          for (let rr = 0; rr < this.board.grid.length; rr++) {
            for (let cc = 0; cc < this.board.grid[0].length; cc++) {
              if (this.board.grid[rr]?.[cc]?.color === col) {
                castR = rr; castC = cc;
                break outer;
              }
            }
          }
        }
        if (castR === -1) return; // no tile of that colour left on board — do nothing
      } else {
        castR = target.r; castC = target.c;
      }
      this._hideCancelButton();
      this._stopColorFlash();
      await this.castSkillAt(monsterId, castR, castC);
    } else {
      this._hideCancelButton();
      this._stopColorFlash();
      this.monsterBar.penalizeEnergyById(monsterId, 0.5);
      this._onSkillFail(monsterId, requiredColor, largestColor);
      this._setStatus(I18n.t('skill_failed', { required: requiredColor.toUpperCase(), actual: String(largestColor).toUpperCase() }), 900);
      this.onMonsterIdle(monsterId);
    }
  }

  spawnScoreFeedback(points, _positions, comboMultiplier, bonus, bestSize) {
    // Texto passa para o HUD (incrText, Osaka font, 1000ms)
    // Board fica limpo — sem texto flutuante sobre as peças
    this.hud?.showScoreIncrement(points, comboMultiplier, bonus, bestSize);
  }

  async castSkillAt(monsterId, r, c) {
    this._hitStop(55);
    // 🔊 SFX: elemento + voz japonesa de ataque (nível 1/2/3)
    const _skillLv = LevelStore.getSkillMultiplier ? LevelStore.getSkillMultiplier(monsterId) : 1;
    this._playSkillSfx(monsterId, _skillLv);
    // 🎆 VFX: burst visual no tile alvo + beam no event strip
    const _wp = this.board.worldPos(r, c);
    spawnSkillBurst(this, _wp.x, _wp.y);
    spawnBeamVFX(this, monsterId, this.eventText.y, _skillLv);

    this.onMonsterCast(monsterId);
    const _castM = this.monsterBar.monsters.find(x => x.id === monsterId);
    // this.showEvent(`✦ ${_castM?.name || monsterId} ataca!`, "#c084fc"); // suspenso — beam é suficiente
    this.monsterBar.consumeEnergyById(monsterId);

    const affected = getAffectedPositions(this.board.grid, monsterId, r, c);
    if (!affected || affected.length === 0) return;

    // ✅ XP: skill conta como progresso do monstro (base = nº tiles afetados)
    this.addXpToMonster(monsterId, affected.length, 1);
    this._refreshMonsterXpBars();

    // ✅ multiplicador por nível do monstro (Lv1 x1, Lv2 x2, Lv3 x3) — só na SKILL
    const mult = LevelStore.getSkillMultiplier ? LevelStore.getSkillMultiplier(monsterId) : 1;
    const baseSkill = Math.floor(affected.length * 8);
    const skillScore = baseSkill * (mult || 1);

    this.score += skillScore;
    this.hud.setScore?.(this.score);
	if (this.goal?.type === "score") {
  this.hud.setGoal(this.goal, this.score);
}

    // Kamikaze label on every skill activation — size and colour scale with level
    {
      const label    = mult >= 3 ? "MEGA ATTACK!" : mult === 2 ? "SUPER ATTACK!" : "ATTACK!";
      const color    = mult >= 3 ? "#ef4444"      : mult === 2 ? "#22c55e"       : "#ffffff";
      const fontSize = mult >= 3 ? "32px"         : mult === 2 ? "26px"          : "20px";
      const txt = this.add.text(
        this.boardX + (this.cols * this.tileSize) / 2,
        this.boardY - 20,
        label,
        {
          fontFamily: "Kamikaze, system-ui, Arial",
          fontSize,
          color,
          stroke: "#0b0f14",
          strokeThickness: 5,
        }
      ).setOrigin(0.5).setDepth(600);
      this.tweens.add({
        targets: txt,
        y: txt.y - 24,
        alpha: 0,
        duration: mult >= 3 ? 700 : mult === 2 ? 560 : 420,
        ease: "Sine.Out",
        onComplete: () => txt.destroy(),
      });
    }

    await this.board.removeTilesAnimated(affected);
    await resolveGravityAndRefill(this.board);

    this.onMonsterIdle(monsterId);
  }

  // -----------------------------------------
  // EVENT STRIP — gap between board and monster bar
  // -----------------------------------------
  showEvent(msg, color = "#e2e8f0") {
    if (!this.eventText) return;
    this.tweens.killTweensOf(this.eventText);
    this.eventText.setText(msg).setColor(color).setAlpha(0).setScale(0.85);
    this.tweens.add({
      targets: this.eventText,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 150,
      ease: "Back.Out",
      onComplete: () => {
        this.tweens.add({
          targets: this.eventText,
          alpha: 0,
          duration: 400,
          delay: 1200,
          ease: "Sine.In",
        });
      },
    });
  }

  // ─── Monster personality helpers ──────────────────────────────────────────

  /** Devolve o Phaser.Image do monstro a partir do seu id. */
  _getMonsterIcon(monsterId) {
    const idx = this.monsterBar.monsters.findIndex(m => m.id === monsterId);
    return idx >= 0 ? this.monsterBar.items[idx]?.icon : null;
  }

  /** Inicia (ou reinicia) o bob idle em loop num ícone. */
  _playIdleAnim(icon) {
    if (!icon?.active) return;
    this.tweens.killTweensOf(icon);
    icon.setY(icon._baseY ?? icon.y);
    // Restaurar escala base (evita ficar com tamanho natural após tweens de cast/fail)
    icon.setScale(icon._baseSX ?? icon.scaleX, icon._baseSY ?? icon.scaleY);
    this.tweens.add({
      targets: icon,
      y: (icon._baseY ?? icon.y) - 5,
      duration: 1400,
      ease: 'Sine.InOut',
      yoyo: true,
      repeat: -1,
    });
  }

  /** Arranca o idle float em todos os monstros, faseado por 250 ms cada. */
  _startIdleAnims() {
    this.monsterBar.monsters.forEach((m, i) => {
      const icon = this.monsterBar.items[i]?.icon;
      if (!icon) return;
      icon._baseY  = icon.y;      // guardar Y base
      icon._baseSX = icon.scaleX; // guardar escala X base (definida por setDisplaySize)
      icon._baseSY = icon.scaleY; // guardar escala Y base
      this.time.delayedCall(i * 250, () => this._playIdleAnim(icon)); // stagger
    });
  }

  // ─── Animation hooks ──────────────────────────────────────────────────────

  /** Retoma o idle após qualquer acção. */
  onMonsterIdle(monsterId) {
    const icon = this._getMonsterIcon(monsterId);
    if (icon) this._playIdleAnim(icon);
  }

  /** Chamado quando o skill é armado — reservado para futura animação "ready". */
  onMonsterReady(_id) {}

  /** Attack lunge: avança 18 px rápido, volta com Elastic.Out. */
  onMonsterCast(monsterId) {
    const icon = this._getMonsterIcon(monsterId);
    if (!icon?.active) return;
    const baseX = icon.x;
    const bsx = icon._baseSX ?? icon.scaleX; // escala base (definida por setDisplaySize)
    const bsy = icon._baseSY ?? icon.scaleY;
    this.tweens.killTweensOf(icon);
    icon.setY(icon._baseY ?? icon.y);
    this.tweens.add({
      targets: icon, x: baseX + 18, scaleX: bsx * 1.15, scaleY: bsy * 1.15,
      duration: 120, ease: 'Expo.Out',
      onComplete: () => {
        this.tweens.add({
          targets: icon, x: baseX, scaleX: bsx, scaleY: bsy, // volta à escala base, não à 1.0
          duration: 280, ease: 'Elastic.Out',
        });
      },
    });
  }

  /** Hit shake: x±8 px × 4 + tint vermelho 300 ms, retoma idle. */
  onMonsterHurt(monsterId) {
    const icon = this._getMonsterIcon(monsterId);
    if (!icon?.active) return;
    const baseX = icon.x;
    this.tweens.killTweensOf(icon);
    icon.setY(icon._baseY ?? icon.y);
    icon.setTint(0xff4444);
    this.time.delayedCall(300, () => { if (icon.active) icon.clearTint(); });
    this.tweens.add({
      targets: icon, x: baseX - 8, duration: 40, ease: 'Linear',
      yoyo: true, repeat: 4,
      onComplete: () => { icon.setX(baseX); this._playIdleAnim(icon); },
    });
  }

  onMonsterDefeated(_id) {}

  // ── Cancel Skill Button ───────────────────────────────────────────────────
  _createCancelButton() {
    const btnX = 55;
    const btnY = this.boardY + this.rows * this.tileSize + 68;
    const R    = 34;

    this._cancelBtn = {
      x: btnX, y: btnY, r: R,
      gfx:  this.add.graphics().setDepth(200),
      text: this.add.text(btnX, btnY, '✕', {
        fontFamily: 'system-ui, Arial',
        fontSize: '26px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3,
      }).setOrigin(0.5).setDepth(201),
      zone: this.add.zone(btnX, btnY, R * 2, R * 2).setInteractive().setDepth(202),
      pulseTween: null,
    };

    this._cancelBtn.zone.on('pointerdown', () => this._onCancelButtonTap());
    this._cancelBtn.gfx.setVisible(false);
    this._cancelBtn.text.setVisible(false);
  }

  _drawCancelButton(colorHex) {
    const { gfx, x, y, r } = this._cancelBtn;
    const color = parseInt(colorHex.replace('#', ''), 16);
    gfx.clear();
    gfx.fillStyle(0x0f172a, 0.92);
    gfx.fillCircle(x, y, r);
    gfx.lineStyle(3, color, 1);
    gfx.strokeCircle(x, y, r);
  }

  _showCancelButton(monsterId) {
    const colorMap = {
      fogo:     '#ff4500',
      raio:     '#facc15',
      jato:     '#06b6d4',
      terra:    '#22c55e',
      controlo: '#c084fc',
    };
    const color = colorMap[monsterId] || '#ffffff';
    this._drawCancelButton(color);
    this._cancelBtn.gfx.setVisible(true).setAlpha(1);
    this._cancelBtn.text.setVisible(true).setAlpha(1);

    if (this._cancelBtn.pulseTween) this._cancelBtn.pulseTween.stop();
    this._cancelBtn.pulseTween = this.tweens.add({
      targets:  [this._cancelBtn.gfx, this._cancelBtn.text],
      alpha:    { from: 1, to: 0.45 },
      duration: 550,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
  }

  // ── Stop colour tile flash and restore scales ────────────────────────────
  _stopColorFlash() {
    if (!this._colorFlashTweens) return;
    for (const t of this._colorFlashTweens) t?.stop();
    this._colorFlashTweens = [];
    // Restore all tile sprites to natural scale
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const tile = this.board.grid[r]?.[c];
        if (tile?.sprite?.active) {
          tile.sprite.setAlpha(1);
          tile.sprite.setScale(1);
        }
      }
    }
  }

  // ── Flash all tiles of a given colour when skill is armed ────────────────
  _flashColorTiles(color) {
    // Kill any previous flash still running
    if (this._colorFlashTweens) {
      for (const t of this._colorFlashTweens) t?.stop();
    }
    this._colorFlashTweens = [];

    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const tile = this.board.grid[r]?.[c];
        if (!tile || tile.color !== color) continue;
        const sprite = tile.sprite;
        if (!sprite?.active) continue;

        // Slow elegant breathe while skill is armed
        const tween = this.tweens.add({
          targets:  sprite,
          alpha:    { from: 1, to: 0.5 },
          duration: 700,
          yoyo:     true,
          repeat:   -1,
          ease:     'Sine.InOut',
          delay:    Phaser.Math.Between(0, 300),
        });
        this._colorFlashTweens.push(tween);
      }
    }
  }

  // ── Hit-stop — freezes all scene tweens briefly via setTimeout (bypasses Phaser time) ──
  _hitStop(ms = 60) {
    this.tweens.timeScale = 0;
    this.time.timeScale   = 0;
    setTimeout(() => {
      if (!this.scene?.isActive('LevelScene')) return;
      this.tweens.timeScale = 1;
      this.time.timeScale   = 1;
    }, ms);
  }

  // ── Skill fail feedback — red flash + energy bar particle burst ──────────────────────
  _onSkillFail(monsterId, requiredColor, largestColor) {
    const W = this.scale.width;
    const H = this.scale.height;
    const D = 1200;

    // Stronger shake than a normal miss
    this.cameras.main.shake(400, 0.022);

    // Red screen flash — lingers longer
    const failFlash = this.add.rectangle(W / 2, H / 2, W, H, 0xff0000, 0).setDepth(D);
    this.tweens.add({
      targets: failFlash, alpha: { from: 0.32, to: 0 },
      duration: 750, ease: 'Quad.Out',
      onComplete: () => failFlash.destroy(),
    });

    // Particles burst from monster energy bar area
    const icon = this._getMonsterIcon(monsterId);
    if (icon?.active) {
      const px = icon.x;
      const py = (icon._baseY ?? icon.y) + 58;
      for (let i = 0; i < 22; i++) {
        const angle = (i / 22) * Math.PI * 2;
        const dist  = Phaser.Math.Between(35, 110);
        const sz    = Phaser.Math.FloatBetween(4, 9);
        const color = i % 2 === 0 ? 0xff4444 : 0xff9900;
        const p = this.add.rectangle(px, py, sz, sz, color, 1).setDepth(D - 1);
        this.tweens.add({
          targets: p,
          x: px + Math.cos(angle) * dist,
          y: py + Math.sin(angle) * dist,
          alpha: 0,
          duration: Phaser.Math.Between(550, 950),
          ease: 'Quad.Out',
          onComplete: () => p.destroy(),
        });
      }
    }

    // MISS text at board centre
    spawnFailEffect(this, W / 2, this.boardY + (this.rows * this.tileSize) / 2);
    this.onMonsterHurt(monsterId);
  }

  _hideCancelButton() {
    if (!this._cancelBtn) return;
    if (this._cancelBtn.pulseTween) { this._cancelBtn.pulseTween.stop(); this._cancelBtn.pulseTween = null; }
    this._cancelBtn.gfx.setVisible(false).setAlpha(1);
    this._cancelBtn.text.setVisible(false).setAlpha(1);
  }

  _onCancelButtonTap() {
    if (this.previewActive) {
      this.cancelSkillPreview();
    } else if (this.armedSkill) {
      const id = this.armedSkill.monsterId;
      this.armedSkill = null;
      this.hud.setStatus('');
      this.onMonsterIdle(id);
      this._hideCancelButton();
    }
  }
}